/**
 * Enhanced Diagram Editor - With all features
 * Properties panel, toolbar, node palette, keyboard shortcuts, undo/redo, export
 */

import { useEffect, useCallback, useState, useRef, useMemo, CSSProperties } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  reconnectEdge,
  Connection,
  OnConnectStart,
  OnConnectEnd,
  NodeTypes,
  EdgeTypes,
  Node,
  Edge,
  ReactFlowProvider,
  useReactFlow,
  useNodesInitialized,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import './styles/editor.css'

import {
  IconLibraryNode,
  ContainerNode,
  TextNode,
  ShapeNode,
  CustomEdge,
  SmartEdge,
  ToolbarV2 as Toolbar,
  PropertiesPanelEnhanced,
  NodePalette,
  MobileToolbar,
  ContainerTemplateSelector,
  ExportOptionsModal,
} from './components'
import DatabaseTableNode from './components/nodes/DatabaseTableNode'
import APIResourceNode from './components/nodes/APIResourceNode'
import APIEndpointNode from './components/nodes/APIEndpointNode'
import UMLClassNode from './components/nodes/UMLClassNode'
import StateNode from './components/nodes/StateNode'
import C4Node from './components/nodes/C4Node'
import SequenceLifelineNode from './components/nodes/SequenceLifelineNode'
import SequenceFragmentNode from './components/nodes/SequenceFragmentNode'
import SequenceNoteNode from './components/nodes/SequenceNoteNode'
import GanttTaskNode from './components/nodes/GanttTaskNode'
import GanttSectionNode from './components/nodes/GanttSectionNode'
import GanttAxisNode from './components/nodes/GanttAxisNode'
import type { ExportOptions } from './components'
import ContextMenu, { buildContextMenuItems } from './components/ContextMenu'
import CommandPalette, { type Command } from './components/CommandPalette'
import OutlinePanel from './components/panels/OutlinePanel'
import LayersPanel from './components/panels/LayersPanel'
import CommentsPanel from './components/panels/CommentsPanel'
import CommentLayer from './components/CommentLayer'
import RulerOverlay from './components/RulerOverlay'
import PresenceCursors from './components/presence/PresenceCursors'
import PresenceAvatars from './components/presence/PresenceAvatars'
import MermaidImportModal from './components/modals/MermaidImportModal'
import DrawioImportModal from './components/modals/DrawioImportModal'
import JSONImportModal from './components/modals/JSONImportModal'
import HelperLines from './components/HelperLines'
import { getHelperLines } from './utils/helperLines'
import {
  Undo2,
  Redo2,
  Download,
  Group as GroupIcon,
  Ungroup,
  Lock,
  Trash2,
  Copy,
  Clipboard,
  Scissors,
  Type as TypeIcon,
  BoxSelect,
  Grid3x3,
  Hand,
  Network,
  Files,
  Paintbrush,
  PaintbrushVertical,
  Pipette,
  AlignHorizontalJustifyStart,
  AlignHorizontalJustifyCenter,
  AlignHorizontalJustifyEnd,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignHorizontalDistributeCenter,
  AlignVerticalDistributeCenter,
  LayoutGrid,
  Moon,
  Sun,
  BringToFront,
  SendToBack,
  ArrowUp,
  ArrowDown,
  Maximize2,
  ImageDown,
  ListTree,
  Ruler,
  Workflow,
  Layers as LayersIcon,
  MessageSquare,
} from 'lucide-react'
import type { IconDefinition } from './config/icons'

import { parseDiagramData, validateDiagramData } from './utils/parser'
import { serializeToDiagramData, exportToJSON, createEditTracker } from './utils/serializer'
import { exportDiagram as exportDiagramByMode, downloadJSON } from './utils/exportModes'
import { autoAssignHandles } from './utils/handle-assignment'
import { assignEdgeOffsets, type NodePosition } from './utils/edge-offset'
import { applyAutoLayout, getRecommendedDirection, needsLayout } from './utils/autolayout'
import { alignNodes, distributeNodes, tidyUpNodes, type AlignEdge, type DistributeAxis } from './utils/arrange'
import { exportToPNG, exportToSVG, exportToPDF, copyDiagramAsImage } from './utils/export'
import { normalizeGridSize } from './utils/grid'
import { buildQuickConnectNode } from './utils/quickConnect'
import { formatZoomPercent } from './utils/zoom'
import { computeNudge } from './utils/keyboardNudge'
import {
  createDiagramStore,
  type DiagramStore,
  useDiagram,
  useDiagramTemporal,
  selectNodes,
  selectEdges,
  selectCanUndo,
  selectCanRedo,
} from './store'
import { useCollab } from './collab/useCollab'
import { ClipboardService, duplicateNodes } from './services/ClipboardService'
import { StyleClipboard } from './services/StyleClipboard'
import { useModal } from './components/modals/ModalProvider'

import type { DiagramData, DiagramEdge, DiagramLayer, DiagramComment, NodeType } from './types/schema'
import {
  createComment,
  addComment as addCommentToList,
  toggleResolved as toggleCommentResolved,
  deleteComment as deleteCommentFromList,
  addReply as addReplyToList,
} from './store/commentStore'
import type { ContainerTemplate } from './templates/containerTemplates'
import { getContainerStyle } from './config/containerStyles'

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
// REMOVED: calculateZIndex and calculateNestingDepth
// Z-index is now set once on creation/grouping, not auto-updated

// Default layer (draw.io-style). When a diagram has no layers, all elements
// belong to this single layer.
const DEFAULT_LAYER_ID = 'layer-1'
const DEFAULT_LAYERS = (): DiagramLayer[] => [
  { id: DEFAULT_LAYER_ID, name: 'Layer 1', visible: true, locked: false },
]

// ============================================================================
// PROPS
// ============================================================================

export interface DiagramEditorEnhancedProps {
  data: DiagramData | null
  onChange?: (data: DiagramData) => void
  onExport?: (json: string) => void
  readonly?: boolean
  showToolbar?: boolean
  showMinimap?: boolean
  showControls?: boolean
  showPropertiesPanel?: boolean
  showNodePalette?: boolean
  onTogglePropertiesPanel?: () => void
  onToggleNodePalette?: () => void
  autoLayout?: boolean
  theme?: 'light' | 'dark'
  className?: string
  style?: CSSProperties
  showGrid?: boolean
  /** Initial camera position/zoom (used as ReactFlow defaultViewport) */
  defaultViewport?: { x: number; y: number; zoom: number }
  /** Called when the camera changes (pan/zoom end) */
  onViewportChange?: (vp: { x: number; y: number; zoom: number }) => void
  /** Called with the ids of currently selected nodes */
  onSelectionChangeIds?: (ids: string[]) => void
  /**
   * Opt-in real-time collaboration. When provided, the editor joins a
   * y-websocket room (per the SHARED ROOM CONTRACT) and the room becomes the
   * source of truth: the `data` prop hydration effect is skipped so prop-sync
   * and the room don't fight. When absent, behavior is identical to before.
   */
  collab?: { url: string; room: string }
}

const nodeTypes: NodeTypes = {
  'icon-library': IconLibraryNode,  // New descriptive name
  'group-container': ContainerNode,  // New descriptive name
  // Backward compatibility - old names still work
  universal: IconLibraryNode,
  container: ContainerNode,
  text: TextNode,
  shape: ShapeNode,
  // Structured Content Nodes (V2)
  'database-table': DatabaseTableNode,
  'api-resource': APIResourceNode,
  'api-endpoint': APIEndpointNode,
  'uml-class': UMLClassNode,
  'state-node': StateNode,
  'c4-node': C4Node,
  'sequence-lifeline': SequenceLifelineNode,
  'sequence-fragment': SequenceFragmentNode,
  'sequence-note': SequenceNoteNode,
  'gantt-task': GanttTaskNode,
  'gantt-section': GanttSectionNode,
  'gantt-axis': GanttAxisNode,
}

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
  // Opt-in obstacle-avoiding orthogonal routing. Default edges stay "custom".
  smart: SmartEdge,
}

// ============================================================================
// MAIN COMPONENT (Wrapped)
// ============================================================================

export default function DiagramEditorEnhanced(props: DiagramEditorEnhancedProps) {
  return (
    <ReactFlowProvider>
      <DiagramEditorInner {...props} />
    </ReactFlowProvider>
  )
}

// ============================================================================
// INNER COMPONENT
// ============================================================================

function DiagramEditorInner({
  data,
  onChange,
  onExport,
  readonly = false,
  showToolbar = true,
  showMinimap = true,
  showControls = true,
  showPropertiesPanel = true,
  showNodePalette = true,
  onTogglePropertiesPanel,
  onToggleNodePalette,
  autoLayout: autoLayoutProp = true,
  theme = 'light',
  className = '',
  style = {},
  showGrid: showGridProp = true,
  defaultViewport,
  onViewportChange,
  onSelectionChangeIds,
  collab,
}: DiagramEditorEnhancedProps) {
  const modal = useModal()
  const reactFlow = useReactFlow()
  const storeRef = useRef<DiagramStore | null>(null)
  if (storeRef.current === null) storeRef.current = createDiagramStore()
  const store = storeRef.current

  const nodes = useDiagram(store, selectNodes)
  const edges = useDiagram(store, selectEdges)
  const canUndo = useDiagramTemporal(store, selectCanUndo)
  const canRedo = useDiagramTemporal(store, selectCanRedo)

  // Opt-in real-time collaboration. When `collab` is absent the hook is inert
  // (enabled=false → no provider, no transport) and behavior is unchanged.
  const collabResult = useCollab(store, {
    url: collab?.url ?? '',
    room: collab?.room ?? '',
    diagramType: data?.diagramType ?? 'architecture',
    enabled: !!collab,
  })
  const { peers: collabPeers, publishPresence } = collabResult
  // Stable handle to publishPresence + the latest selection so the high-frequency
  // pane-mousemove handler can publish the local cursor without re-subscribing.
  const publishPresenceRef = useRef(publishPresence)
  useEffect(() => {
    publishPresenceRef.current = publishPresence
  }, [publishPresence])
  const selectionIdsRef = useRef<string[]>([])

  const onNodesChange = useCallback(
    (changes: any) => store.getState().onNodesChange(changes),
    [store],
  )
  const onEdgesChange = useCallback(
    (changes: any) => store.getState().onEdgesChange(changes),
    [store],
  )
  // React-Flow-style setters (accept an array OR an updater fn). Tracked = goes
  // into undo history; Silent = does not (use for selection/flag-only updates).
  const setNodes = useCallback(
    (u: Node[] | ((n: Node[]) => Node[])) => {
      const next = typeof u === 'function' ? (u as (n: Node[]) => Node[])(store.getState().nodes) : u
      store.getState().setNodes(next)
    },
    [store],
  )
  const setEdges = useCallback(
    (u: Edge[] | ((e: Edge[]) => Edge[])) => {
      const next = typeof u === 'function' ? (u as (e: Edge[]) => Edge[])(store.getState().edges) : u
      store.getState().setEdges(next)
    },
    [store],
  )
  const setNodesSilent = useCallback(
    (u: Node[] | ((n: Node[]) => Node[])) => {
      const next = typeof u === 'function' ? (u as (n: Node[]) => Node[])(store.getState().nodes) : u
      store.getState().setNodesSilent(next)
    },
    [store],
  )
  const setEdgesSilent = useCallback(
    (u: Edge[] | ((e: Edge[]) => Edge[])) => {
      const next = typeof u === 'function' ? (u as (e: Edge[]) => Edge[])(store.getState().edges) : u
      store.getState().setEdgesSilent(next)
    },
    [store],
  )
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([])
  const [selectedEdges, setSelectedEdges] = useState<Edge[]>([])
  const [showGrid, setShowGrid] = useState(showGridProp)
  const [isPanMode, setIsPanMode] = useState(false) // Pan mode vs Selection mode

  // Live zoom factor for the on-canvas zoom-percentage readout. Initialized
  // from the incoming defaultViewport (falls back to 1 = 100%) and kept current
  // via the ReactFlow onMove handler.
  const [zoom, setZoom] = useState<number>(defaultViewport?.zoom ?? 1)

  // Snap-to-grid (React Flow snapToGrid/snapGrid). Both are additive document
  // fields; defaults are snap off + a sensible grid size. Hydrated from `data`
  // and persisted back through the serialize callbacks.
  const [snapToGrid, setSnapToGrid] = useState<boolean>(data?.snapToGrid ?? false)
  const [gridSize, setGridSize] = useState<number>(normalizeGridSize(data?.gridSize))
  // Always-current snapshot so the serialize callbacks can persist the grid
  // settings without taking them as effect/callback dependencies.
  const gridStateRef = useRef({ snapToGrid, gridSize })
  useEffect(() => {
    gridStateRef.current = { snapToGrid, gridSize }
  }, [snapToGrid, gridSize])

  // Container management
  const [showContainerModal, setShowContainerModal] = useState(false)

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFormat, setExportFormat] = useState<'png' | 'svg' | 'json'>('png')

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)

  // Command palette (Cmd/Ctrl+K)
  const [showCommandPalette, setShowCommandPalette] = useState(false)

  // Outline / shapes-tree side panel (toggled from the View command group)
  const [showOutline, setShowOutline] = useState(false)

  // Canvas rulers overlay (toggled from the View command group)
  const [showRulers, setShowRulers] = useState(false)

  // Mermaid flowchart import modal (Insert command group)
  const [showMermaidImport, setShowMermaidImport] = useState(false)

  // draw.io / mxGraph XML import modal (Insert command group)
  const [showDrawioImport, setShowDrawioImport] = useState(false)

  // JSON / file-open import modal (Insert command group)
  const [showJsonImport, setShowJsonImport] = useState(false)

  // Layers (draw.io-style). `layers` is back-to-front; `activeLayerId` is where
  // new elements land. Always at least one layer. Toggled via the View command.
  const [layers, setLayers] = useState<DiagramLayer[]>(() => DEFAULT_LAYERS())
  const [activeLayerId, setActiveLayerId] = useState<string>(DEFAULT_LAYER_ID)
  const [showLayers, setShowLayers] = useState(false)
  // Always-current snapshot of layers so the various onChange serialization
  // callbacks can persist them without taking `layers` as a dependency.
  const layersRef = useRef<DiagramLayer[]>(layers)
  useEffect(() => {
    layersRef.current = layers
  }, [layers])

  // Comments / annotation threads (anchored, resolvable). Document-level state
  // like layers — kept out of the zundo node/edge store and persisted via
  // commentsRef in the serialization callbacks.
  const [comments, setComments] = useState<DiagramComment[]>(() => data?.comments ?? [])
  const [showComments, setShowComments] = useState(false)
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null)
  const commentsRef = useRef<DiagramComment[]>(comments)
  useEffect(() => {
    commentsRef.current = comments
  }, [comments])

  // Presentation / fullscreen mode — hides all chrome and (best-effort) requests
  // browser fullscreen on the editor root. Synced with the Fullscreen API so
  // leaving fullscreen via the browser (Esc/F11) also exits presentation mode.
  const [isPresentationMode, setIsPresentationMode] = useState(false)
  const editorRootRef = useRef<HTMLDivElement | null>(null)

  // Active theme — lifted into state so the "Toggle dark mode" command can flip
  // data-theme + React Flow colorMode live. Initialized from the theme prop and
  // kept in sync if the prop changes.
  const [activeTheme, setActiveTheme] = useState<'light' | 'dark'>(theme)
  useEffect(() => {
    setActiveTheme(theme)
  }, [theme])

  // Smart alignment guides (Figma-style helper lines) shown while dragging.
  const [helperLineHorizontal, setHelperLineHorizontal] = useState<number | undefined>(undefined)
  const [helperLineVertical, setHelperLineVertical] = useState<number | undefined>(undefined)

  // History for undo/redo is now managed by the zustand+zundo store
  const isUndoRedoRef = useRef(false)
  const isSyncingFromPropsRef = useRef(false) // Flag to prevent onChange during prop sync
  const hasInitializedRef = useRef(false) // Track if we've done initial fitView
  const lastDataRef = useRef<DiagramData | null>(null) // Track last data to prevent re-sync loops
  const isLocalChangeRef = useRef(false) // Track if change originated from this component

  // Clipboard service for copy/paste/duplicate
  const clipboardService = ClipboardService.getInstance()
  const styleClipboard = StyleClipboard.getInstance()
  // Bump on copy/paste-style so the command palette re-evaluates `hasStyle()`.
  const [styleBufferVersion, setStyleBufferVersion] = useState(0)

  // Track Alt+Click selection to prevent re-selection on drag
  const altClickSelectedRef = useRef<string | null>(null)

  // ============================================================================
  // PARSE DIAGRAM DATA
  // ============================================================================

  useEffect(() => {
    // When collaboration is active the room is the source of truth — skip the
    // `data` prop hydration so prop-sync and the room don't fight each other.
    if (collab) return

    // Skip if this is a local change (from our own onChange callback)
    if (isLocalChangeRef.current) {
      console.log('[DiagramEditor] Skipping data sync - local change')
      isLocalChangeRef.current = false
      lastDataRef.current = data
      return
    }

    // Skip if data hasn't actually changed (prevent re-sync from our own onChange)
    // Compare structure (ignore position changes)
    const hasStructuralChange = () => {
      if (!data && !lastDataRef.current) return false
      if (!data || !lastDataRef.current) return true

      // Compare node/edge counts and IDs
      const currentIds = new Set(data.nodes.map(n => n.id))
      const lastIds = new Set(lastDataRef.current.nodes.map(n => n.id))

      if (currentIds.size !== lastIds.size) return true
      for (const id of currentIds) {
        if (!lastIds.has(id)) return true
      }

      const currentEdgeIds = new Set((data.edges || []).map(e => `${e.source}-${e.target}`))
      const lastEdgeIds = new Set((lastDataRef.current.edges || []).map(e => `${e.source}-${e.target}`))

      if (currentEdgeIds.size !== lastEdgeIds.size) return true
      for (const id of currentEdgeIds) {
        if (!lastEdgeIds.has(id)) return true
      }

      return false
    }

    if (!hasStructuralChange()) {
      lastDataRef.current = data
      return
    }
    lastDataRef.current = data

    if (!data) {
      isSyncingFromPropsRef.current = true
      setNodesSilent([])
      setEdgesSilent([])
      hasInitializedRef.current = false
      setTimeout(() => { isSyncingFromPropsRef.current = false }, 150)
      return
    }

    const validation = validateDiagramData(data)
    if (!validation.valid) {
      console.error('Invalid diagram data:', validation.errors)
      return
    }

    // Hydrate snap-to-grid settings from the document (additive, optional).
    if (typeof data.snapToGrid === 'boolean') setSnapToGrid(data.snapToGrid)
    if (data.gridSize !== undefined) setGridSize(normalizeGridSize(data.gridSize))

    // Hydrate layers from the document (draw.io-style). Fall back to a single
    // default layer when the document doesn't declare any.
    if (data.layers && data.layers.length > 0) {
      setLayers(data.layers)
      setActiveLayerId((prev) =>
        data.layers!.some((l) => l.id === prev) ? prev : data.layers![0].id,
      )
    } else {
      setLayers(DEFAULT_LAYERS())
      setActiveLayerId(DEFAULT_LAYER_ID)
    }

    // Hydrate comments from the document (additive, optional).
    setComments(data.comments ?? [])

    const parsed = parseDiagramData(data)
    let { nodes: parsedNodes, edges: parsedEdges } = parsed

    // Normalize edge types: default to the enhanced 'custom' edge, but preserve
    // an explicit opt-in 'smart' routing choice so it survives structural
    // prop-sync (reload / re-import / parent-driven re-sync).
    parsedEdges = parsedEdges.map(edge => ({
      ...edge,
      type: edge.type === 'smart' ? 'smart' : 'custom'
    }))

    // Check if auto-layout was applied (for edge refresh)
    let wasLayoutApplied = false

    // Sequence diagrams are pre-positioned by buildSequenceDiagram (time-ordered
    // rows / fixed columns); dagre would destroy that, so never auto-layout them.
    if (autoLayoutProp && data.diagramType !== 'sequence' && data.diagramType !== 'gantt' && needsLayout(parsedNodes)) {
      const direction = data.layout?.direction || getRecommendedDirection(data.diagramType)
      parsedNodes = applyAutoLayout(parsedNodes, parsedEdges, {
        algorithm: data.layout?.algorithm || 'dagre',
        direction,
        spacing: data.layout?.spacing,
      })
      wasLayoutApplied = true
    }
    // Set flag to prevent onChange callbacks during prop sync
    isSyncingFromPropsRef.current = true
    setNodesSilent(parsedNodes)
    setEdgesSilent(parsedEdges)
    store.getState().clearHistory()

    // Clear flag after state updates
    setTimeout(() => {
      isSyncingFromPropsRef.current = false

      // Mark as initialized
      if (parsedNodes.length > 0) {
        hasInitializedRef.current = true

      // If auto-layout was applied, force edge refresh to fix label positions
      if (wasLayoutApplied) {
        reactFlow.fitView({ padding: 0.1, duration: 400 })
        console.log('[DiagramEditor] Auto-layout applied on import, edges refreshed')
      }
      }
    }, 150)
  }, [data, autoLayoutProp, reactFlow, collab])

  // Fit the view ONCE the nodes have real measured dimensions, but only when the
  // canvas opened WITHOUT a saved camera (e.g. opening a showcase example or a
  // fresh tab). This centers pre-positioned diagrams (which skip auto-layout, so
  // the load effect above never fit them) without overriding a user's saved
  // viewport on persisted/collab tabs. Runs once per mount (editor is keyed per tab).
  const nodesInitialized = useNodesInitialized()
  const didAutoFitRef = useRef(false)
  useEffect(() => {
    if (didAutoFitRef.current) return
    if (defaultViewport) { didAutoFitRef.current = true; return } // respect saved camera
    if (!nodesInitialized) return
    if (reactFlow.getNodes().length === 0) return
    didAutoFitRef.current = true
    // Fit once dimensions are measured. Large custom nodes (e.g. UML class tables)
    // can re-measure as their content reflows AFTER the first fit, leaving a stale
    // zoom — so we fit on the next frame and again shortly after to settle on the
    // final bounding box. `false` skips the animation on the retry to avoid a visible
    // re-zoom jump.
    const fit = (animate: boolean) =>
      reactFlow.fitView({ padding: 0.18, duration: animate ? 400 : 0 })
    requestAnimationFrame(() => {
      fit(true)
      setTimeout(() => fit(false), 260)
    })
  }, [nodesInitialized, defaultViewport, reactFlow])

  // ============================================================================
  // SELECTION
  // ============================================================================

  const onSelectionChange = useCallback(({ nodes: selectedNodes, edges: selectedEdges }: any) => {
    setSelectedNodes(selectedNodes || [])
    setSelectedEdges(selectedEdges || [])
    const ids = (selectedNodes || []).map((n: Node) => n.id)
    selectionIdsRef.current = ids
    onSelectionChangeIds?.(ids)
    // Publish the local selection to peers (no-op when collab is off).
    if (collab) {
      publishPresenceRef.current({ selection: ids })
    }
  }, [onSelectionChangeIds, collab])

  // Publish the local cursor (flow coords) + current selection to peers. Gated
  // on `collab`, so this is inert (and not even attached) when collab is off.
  const onPaneMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!collab) return
      const cursor = reactFlow.screenToFlowPosition({ x: event.clientX, y: event.clientY })
      publishPresenceRef.current({ cursor, selection: selectionIdsRef.current })
    },
    [collab, reactFlow],
  )

  // Make only selected nodes draggable to prevent wrong nodes from being dragged
  useEffect(() => {
    const selectedIds = new Set(selectedNodes.map((n: Node) => n.id))
    // Include Alt+Click selected node even if not in selectedNodes state yet
    if (altClickSelectedRef.current) {
      selectedIds.add(altClickSelectedRef.current)
    }
    const hasSelection = selectedIds.size > 0

    setNodesSilent((nds) =>
      nds.map((node) => {
        // Only update draggable property, don't shallow spread entire node
        const newDraggable = !readonly &&
                             !(node.data as any)?.locked &&
                             (!hasSelection || selectedIds.has(node.id))

        // Only update if draggable actually changed
        if (node.draggable === newDraggable) return node

        return {
          ...node,
          draggable: newDraggable
        }
      })
    )
  }, [selectedNodes, setNodesSilent, readonly])

  // ============================================================================
  // SMART CONTAINER SELECTION (Options A + D)
  // ============================================================================

  const handleNodeClick = useCallback((event: React.MouseEvent, clickedNode: Node) => {
    const isAltClick = event.altKey

    // Clear alt-click lock if not alt-clicking
    if (!isAltClick) {
      altClickSelectedRef.current = null
    }

    // Get all nodes at this position (overlapping)
    const clickX = event.clientX
    const clickY = event.clientY
    const overlappingNodes = nodes.filter(node => {
      const element = document.querySelector(`[data-id="${node.id}"]`)
      if (!element) return false
      const bounds = element.getBoundingClientRect()
      return clickX >= bounds.left && clickX <= bounds.right &&
             clickY >= bounds.top && clickY <= bounds.bottom
    })

    // Sort by z-index (lowest first for cycling)
    overlappingNodes.sort((a, b) => {
      const aIndex = typeof a.style?.zIndex === 'number' ? a.style.zIndex : 0
      const bIndex = typeof b.style?.zIndex === 'number' ? b.style.zIndex : 0
      return aIndex - bIndex
    })

    // OPTION A: Alt+Click cycles through overlapping
    if (isAltClick && overlappingNodes.length > 1) {
      const currentIndex = overlappingNodes.findIndex(n => n.id === clickedNode.id)
      const nextIndex = (currentIndex + 1) % overlappingNodes.length
      const nextNode = overlappingNodes[nextIndex]

      // Select next in cycle and LOCK this selection
      altClickSelectedRef.current = nextNode.id
      setNodesSilent(nds => nds.map(n => ({
        ...n,
        selected: n.id === nextNode.id,
        draggable: !readonly &&
                   !(n.data as any)?.locked &&
                   n.id === nextNode.id
      })))
      event.stopPropagation()
      return
    }

    // OPTION D: Smart edge vs center click (only for containers)
    if (clickedNode.type === 'group-container' || clickedNode.type === 'container') {
      const element = document.querySelector(`[data-id="${clickedNode.id}"]`)
      if (element) {
        const bounds = element.getBoundingClientRect()
        const relativeX = clickX - bounds.left
        const relativeY = clickY - bounds.top

        // Smart threshold: proportional to container size, min 20px, max 50px
        const edgeThreshold = Math.min(50, Math.max(20, Math.min(bounds.width, bounds.height) * 0.15))

        // Check if clicking on header (always selects container)
        const headerHeight = 40 // Standard container header height
        const isHeaderClick = relativeY < headerHeight

        const isEdgeClick = isHeaderClick ||
          relativeX < edgeThreshold ||
          relativeX > bounds.width - edgeThreshold ||
          relativeY < edgeThreshold ||
          relativeY > bounds.height - edgeThreshold

        if (isEdgeClick) {
          // Edge click → select this container
          setNodesSilent(nds => nds.map(n => ({ ...n, selected: n.id === clickedNode.id })))
          event.stopPropagation()
          return
        } else {
          // Center click → find deepest overlapping node
          const innerNodes = overlappingNodes.filter(n =>
            n.id !== clickedNode.id &&
            (n.style?.zIndex || 0) > (clickedNode.style?.zIndex || 0)
          )

          if (innerNodes.length > 0) {
            // Select deepest (highest z-index)
            const deepest = innerNodes.reduce((prev, curr) =>
              ((curr.style?.zIndex || 0) > (prev.style?.zIndex || 0)) ? curr : prev
            )
            setNodesSilent(nds => nds.map(n => ({ ...n, selected: n.id === deepest.id })))
            event.stopPropagation()
            return
          }
        }
      }
    }

    // Default: let React Flow handle it
  }, [nodes, setNodesSilent])

  // ============================================================================
  // NODE & EDGE UPDATES
  // ============================================================================

  const handleNodesChangeCallback = useCallback(
    (changes: any) => {
      // Smart alignment guides + snapping (Figma-style helper lines).
      // Only engage for a single dragging position change so we don't
      // interfere with multi-select drags or other change types.
      const positionChanges = changes.filter(
        (c: any) => c.type === 'position' && c.dragging === true && c.position
      )

      if (positionChanges.length === 1) {
        const helperLines = getHelperLines(positionChanges[0], reactFlow.getNodes() as any)
        // Snap the dragged node to the nearest guide(s).
        positionChanges[0].position = helperLines.snapPosition
        setHelperLineHorizontal(helperLines.horizontal)
        setHelperLineVertical(helperLines.vertical)
      } else {
        // Drag ended or a non-single-drag change: clear the guides.
        if (helperLineHorizontal !== undefined) setHelperLineHorizontal(undefined)
        if (helperLineVertical !== undefined) setHelperLineVertical(undefined)
      }

      onNodesChange(changes)

      // Skip onChange callbacks during special operations
      if (isUndoRedoRef.current || isSyncingFromPropsRef.current) {
        console.log('[DiagramEditor] Skipping nodes change callback - undo/redo or syncing')
        return
      }

      // Check if we're currently dragging (position change with dragging=true)
      const isCurrentlyDragging = changes.some((change: any) =>
        change.type === 'position' && change.dragging === true
      )

      // Check if drag just ended (position change with dragging=false and positionAbsolute defined)
      const dragJustEnded = changes.some((change: any) =>
        change.type === 'position' && change.dragging === false && change.position
      )

      // Check for other meaningful changes
      const hasOtherChanges = changes.some((change: any) =>
        change.type === 'add' || change.type === 'remove' || change.type === 'dimensions'
      )

      // Don't do anything while actively dragging
      if (isCurrentlyDragging) {
        return
      }

      // Save to history and trigger onChange when drag ends or other changes occur
      if (dragJustEnded || hasOtherChanges) {
        // Use a short delay to batch multiple rapid changes
        setTimeout(() => {
          // Double-check flag inside setTimeout (timing race protection)
          if (isUndoRedoRef.current || isSyncingFromPropsRef.current) {
            console.log('[DiagramEditor] Skipping delayed push - undo/redo or syncing')
            return
          }

          const currentNodes = reactFlow.getNodes()
          const currentEdges = reactFlow.getEdges()

          if (onChange && data) {
            isLocalChangeRef.current = true
            const updatedData = serializeToDiagramData(currentNodes, currentEdges, data.diagramType, { layers: layersRef.current, comments: commentsRef.current, ...gridStateRef.current })
            onChange(updatedData)
          }
        }, 100)
      }
    },
    [onNodesChange, onChange, data, reactFlow, helperLineHorizontal, helperLineVertical]
  )

  const handleEdgesChangeCallback = useCallback(
    (changes: any) => {
      onEdgesChange(changes)

      // Don't trigger onChange if we're syncing from props or doing undo/redo
      if (!isUndoRedoRef.current && !isSyncingFromPropsRef.current) {
        setTimeout(() => {
          // Double-check flag inside setTimeout (timing race protection)
          if (isUndoRedoRef.current || isSyncingFromPropsRef.current) {
            console.log('[DiagramEditor] Skipping delayed edge push - undo/redo or syncing')
            return
          }

          const currentNodes = reactFlow.getNodes()
          const currentEdges = reactFlow.getEdges()

          if (onChange && data) {
            isLocalChangeRef.current = true
            const updatedData = serializeToDiagramData(currentNodes, currentEdges, data.diagramType, { layers: layersRef.current, comments: commentsRef.current, ...gridStateRef.current })
            onChange(updatedData)
          }
        }, 100)
      }
    },
    [onEdgesChange, onChange, data, reactFlow]
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      if (readonly) return
      setEdges((eds) =>
        addEdge(
          { ...connection, type: 'custom', data: { layerId: activeLayerId } },
          eds,
        ),
      )
    },
    [readonly, setEdges, activeLayerId]
  )

  // Quick-connect: dragging a connection onto empty canvas creates a new
  // connected node at the drop point (draw.io / Excalidraw staple). We remember
  // where the drag started in onConnectStart, then create node+edge in
  // onConnectEnd when it lands on the pane (no valid target handle).
  const connectingFrom = useRef<{ nodeId: string | null; handleId: string | null } | null>(null)

  const onConnectStart = useCallback<OnConnectStart>(
    (_event, params) => {
      connectingFrom.current = { nodeId: params.nodeId, handleId: params.handleId }
    },
    []
  )

  const onConnectEnd = useCallback<OnConnectEnd>(
    (event) => {
      const started = connectingFrom.current
      connectingFrom.current = null
      if (readonly || !started?.nodeId) return

      // Only quick-connect when the drag ended on empty canvas (the pane), not
      // on another node/handle (React Flow's onConnect handles those).
      const targetEl = event.target as HTMLElement | null
      const droppedOnPane = !!targetEl?.classList?.contains('react-flow__pane')
      if (!droppedOnPane) return

      const point =
        'changedTouches' in event && event.changedTouches.length > 0
          ? event.changedTouches[0]
          : (event as MouseEvent)
      const flow = reactFlow.screenToFlowPosition({ x: point.clientX, y: point.clientY })

      const { snapToGrid: snap, gridSize: size } = gridStateRef.current
      const { node, edge } = buildQuickConnectNode({
        sourceId: started.nodeId,
        dropFlowX: flow.x,
        dropFlowY: flow.y,
        gridSize: snap ? size : 0,
        layerId: activeLayerId,
        sourceHandle: started.handleId ?? undefined,
        nodeType: (data?.diagramType === 'flowchart' ? 'process' : 'component'),
      })

      setNodes((nds) => [...nds, node as unknown as Node])
      setEdges((eds) => [...eds, edge as unknown as Edge])
    },
    [readonly, reactFlow, activeLayerId, data, setNodes, setEdges]
  )

  // Edge reconnection handlers
  const edgeReconnectSuccessful = useRef(true)

  const onReconnectStart = useCallback(() => {
    edgeReconnectSuccessful.current = false
  }, [])

  const onReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      if (readonly) return
      edgeReconnectSuccessful.current = true
      setEdges((els) => reconnectEdge(oldEdge, newConnection, els))
    },
    [readonly, setEdges]
  )

  const onReconnectEnd = useCallback(
    (_: any, _edge: Edge) => {
      if (readonly) return
      // Intentionally do NOT delete an edge whose endpoint was dropped in empty
      // space. React Flow's default example deletes it; we keep it instead so the
      // arrow snaps back to its original connection and stays editable/reconnectable.
      // Edges are only removed via explicit delete (Delete key / right-click).
      edgeReconnectSuccessful.current = true
    },
    [readonly]
  )

  // ============================================================================
  // PROPERTIES PANEL
  // ============================================================================

  const handleNodeUpdate = useCallback((nodeId: string, updates: Partial<Node>) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== nodeId) return node

        // Deep merge data and style to prevent losing existing properties
        const updatedNode = { ...node, ...updates }
        if (updates.data) {
          updatedNode.data = { ...node.data, ...updates.data }
        }
        if (updates.style) {
          updatedNode.style = { ...node.style, ...updates.style }
        }

        return updatedNode
      })
    )

    // Save to history and trigger onChange
    setTimeout(() => {
      // Double-check flag inside setTimeout (timing race protection)
      if (isUndoRedoRef.current || isSyncingFromPropsRef.current) {
        console.log('[DiagramEditor] Skipping delayed node update push - undo/redo or syncing')
        return
      }

      const currentNodes = reactFlow.getNodes()
      const currentEdges = reactFlow.getEdges()

      if (onChange && data) {
        isLocalChangeRef.current = true
        const updatedData = serializeToDiagramData(currentNodes, currentEdges, data.diagramType, { layers: layersRef.current, comments: commentsRef.current, ...gridStateRef.current })
        onChange(updatedData)
      }
    }, 50)
  }, [setNodes, reactFlow, onChange, data])

  /**
   * Bulk-update the `data` object of many nodes at once (multi-select styling).
   * Uses the tracked `setNodes` so the whole change lands as a single undo step,
   * then mirrors the onChange/serialize behaviour of `handleNodeUpdate`.
   */
  const handleNodesUpdate = useCallback((ids: string[], dataUpdates: Record<string, any>) => {
    const idSet = new Set(ids)
    setNodes((nds) =>
      nds.map((node) =>
        idSet.has(node.id)
          ? { ...node, data: { ...node.data, ...dataUpdates } }
          : node
      )
    )

    setTimeout(() => {
      if (isUndoRedoRef.current || isSyncingFromPropsRef.current) return

      const currentNodes = reactFlow.getNodes()
      const currentEdges = reactFlow.getEdges()

      if (onChange && data) {
        isLocalChangeRef.current = true
        const updatedData = serializeToDiagramData(currentNodes, currentEdges, data.diagramType, { layers: layersRef.current, comments: commentsRef.current, ...gridStateRef.current })
        onChange(updatedData)
      }
    }, 50)
  }, [setNodes, reactFlow, onChange, data])

  const handleEdgeUpdate = useCallback((edgeId: string, updates: Partial<Edge>) => {
    console.log('[Edge Update]', { edgeId, updates })
    setEdges((eds) =>
      {
        const mappedEdges = eds.map((edge) => {
          if (edge.id !== edgeId) return edge

          // Deep merge data and style to prevent losing existing properties
          const updatedEdge = { ...edge, ...updates }
          if (updates.data) {
            updatedEdge.data = { ...edge.data, ...updates.data }
          }
          if (updates.style) {
            updatedEdge.style = { ...edge.style, ...updates.style }
          }

          console.log('[Edge After Update]', { old: edge, new: updatedEdge })
          return updatedEdge
        })

        const nodePositions = buildNodePositions(reactFlow.getNodes())
        if (!nodePositions.length) {
          return mappedEdges
        }

        const processedMap = buildProcessedEdgeMap(mappedEdges, nodePositions)

        return mappedEdges.map((edge) => {
          if (!edge.id) {
            return edge
          }

          const processed = processedMap.get(edge.id)
          if (!processed) {
            return edge
          }

          return {
            ...edge,
            sourceHandle: processed.sourceHandle ?? edge.sourceHandle,
            targetHandle: processed.targetHandle ?? edge.targetHandle,
            data: {
              ...edge.data,
              ...processed.data,
            },
          }
        })
      }
    )

    // Trigger onChange after edge update
    setTimeout(() => {
      // Double-check flag inside setTimeout (timing race protection)
      if (isUndoRedoRef.current || isSyncingFromPropsRef.current) {
        console.log('[DiagramEditor] Skipping delayed edge update - undo/redo or syncing')
        return
      }

      const currentNodes = reactFlow.getNodes()
      const currentEdges = reactFlow.getEdges()

      if (onChange && data) {
        isLocalChangeRef.current = true
        const updatedData = serializeToDiagramData(currentNodes, currentEdges, data.diagramType, { layers: layersRef.current, comments: commentsRef.current, ...gridStateRef.current })
        onChange(updatedData)
      }
    }, 50)
  }, [setEdges, reactFlow, onChange, data])

  // ============================================================================
  // ADD NODE
  // ============================================================================

  // Helper function to get sample data for technical nodes
  const getTechnicalNodeData = (type: 'database-table' | 'api-resource' | 'api-endpoint' | 'uml-class' | 'state-node' | 'c4-node') => {
    switch (type) {
      case 'c4-node':
        return {
          label: 'Banking System',
          c4Content: {
            elementType: 'system' as const,
            name: 'Banking System',
            technology: 'Spring Boot',
            description: 'Allows customers to manage accounts',
          },
        }
      case 'state-node':
        return {
          label: 'Active',
          stateContent: {
            stateName: 'Active',
            kind: 'simple' as const,
            internalTransitions: ['entry / start', 'exit / stop'],
          },
        }
      case 'uml-class':
        return {
          label: 'Animal',
          classContent: {
            className: 'Animal',
            stereotype: 'abstract',
            attributes: [
              { visibility: '+' as const, name: 'name', type: 'String' },
              { visibility: '-' as const, name: 'age', type: 'int' },
            ],
            methods: [
              { visibility: '+' as const, signature: 'isMammal(): bool' },
              { visibility: '#' as const, signature: 'move(): void', isAbstract: true },
            ],
          },
        }
      case 'database-table':
        return {
          label: 'Users',
          tableContent: {
            tableName: 'users',
            columns: [
              { name: 'id', dataType: 'INT', isPrimaryKey: true, isNotNull: true },
              { name: 'email', dataType: 'VARCHAR(255)', isNotNull: true, isUnique: true },
              { name: 'username', dataType: 'VARCHAR(100)', isNotNull: true },
              { name: 'created_at', dataType: 'TIMESTAMP', defaultValue: 'CURRENT_TIMESTAMP' },
            ],
          },
        }
      case 'api-resource':
        return {
          label: 'UserResource',
          resourceContent: {
            stereotype: 'resource',
            resourceName: 'User',
            attributes: ['id: string', 'email: string', 'username: string'],
            methods: [
              { type: 'GET' as const, signature: 'getUser(id)' },
              { type: 'POST' as const, signature: 'createUser(data)' },
              { type: 'PUT' as const, signature: 'updateUser(id, data)' },
              { type: 'DELETE' as const, signature: 'deleteUser(id)' },
            ],
          },
        }
      case 'api-endpoint':
        return {
          label: 'USER API',
          endpointContent: {
            entityName: 'USER',
            description: 'User management endpoints',
            endpoints: [
              {
                method: 'GET' as const,
                items: [
                  { path: '/api/users', description: 'Get all users' },
                  { path: '/api/users/:id', description: 'Get user by ID' },
                ],
              },
              {
                method: 'POST' as const,
                items: [
                  { path: '/api/users', description: 'Create new user' },
                ],
              },
              {
                method: 'PUT' as const,
                items: [
                  { path: '/api/users/:id', description: 'Update user' },
                ],
              },
              {
                method: 'DELETE' as const,
                items: [
                  { path: '/api/users/:id', description: 'Delete user' },
                ],
              },
            ],
          },
        }
    }
  }

  const handleAddNode = useCallback((nodeType: NodeType, iconData?: IconDefinition, shapeData?: any, technicalType?: 'database-table' | 'api-resource' | 'api-endpoint' | 'uml-class' | 'state-node' | 'c4-node', position?: { x: number; y: number }) => {
    let nodePosition = position

    // If no position provided, add in center of current viewport
    if (!nodePosition) {
      const viewport = reactFlow.getViewport()
      const viewportCenter = {
        x: (window.innerWidth / 2 - viewport.x) / viewport.zoom,
        y: (window.innerHeight / 2 - viewport.y) / viewport.zoom,
      }

      // Add some randomness around the center
      nodePosition = {
        x: viewportCenter.x + Math.random() * 100 - 50,
        y: viewportCenter.y + Math.random() * 100 - 50,
      }
    }

    // Handle technical nodes (V2 structured content)
    if (technicalType) {
      const techNode: Node = {
        id: `node-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        type: technicalType,
        position: nodePosition,
        data: { ...getTechnicalNodeData(technicalType), layerId: activeLayerId },
        // No fixed width/height - let React Flow measure the content
        style: {},
        selectable: !readonly,
        deletable: !readonly,
      }
      setNodes((nds) => [...nds, techNode])
      return
    }

    const newNode: Node = {
      id: `node-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      // Use 'shape' type for shapes, 'universal' for icons
      type: shapeData ? 'shape' : 'universal',
      position: nodePosition,
      data: {
        // Shapes don't have labels by default (only show if user adds custom text)
        label: shapeData ? '' : (iconData?.name || (nodeType.charAt(0).toUpperCase() + nodeType.slice(1))),
        nodeType,
        diagramType: data?.diagramType || 'architecture',
        // Use shape path if provided, otherwise icon path
        icon: shapeData?.path || iconData?.path || getIconForNodeType(nodeType),
        // Store original icon for "Restore to Default" functionality
        originalIcon: shapeData?.path || iconData?.path || getIconForNodeType(nodeType),
        iconId: iconData?.id,
        provider: iconData?.provider,
        // Shape-specific data
        shapeId: shapeData?.id,
        shapeCategory: shapeData?.category,
        isShape: !!shapeData,
        // Layer assignment (draw.io-style layers)
        layerId: activeLayerId,
        // Default styles
        // Shapes: No fill, with border, no shadows
        // Regular nodes (AWS, Azure, etc.): No fill, no border, no shadows
        ...(shapeData ? {
          bgColor: 'transparent',  // No fill by default (matches StyleTab logic)
          borderColor: '#000000',  // Black border
          borderWidth: 2,          // 2px border width
          fillOpacity: 1,          // Full opacity when fill is enabled
          shadow: false,           // No shadow by default
        } : {
          bgColor: 'transparent',  // No fill for regular nodes
          borderColor: 'transparent',  // No border for regular nodes
          borderWidth: 0,          // No border
          fillOpacity: 1,          // Full opacity when fill is enabled
          shadow: false,           // No shadow
        }),
      },
      // Set actual dimensions for resizing to work
      width: 120,
      height: 120,
      style: {},
      selectable: !readonly,
      deletable: !readonly,
    }

    setNodes((nds) => [...nds, newNode])

    // DON'T call fitView - let user control the viewport
  }, [setNodes, data, reactFlow, readonly, activeLayerId])

  // ============================================================================
  // CONTAINER CREATION
  // ============================================================================

  const handleCreateContainer = useCallback((template: ContainerTemplate) => {
    const viewport = reactFlow.getViewport()
    const centerPosition = {
      x: (window.innerWidth / 2 - viewport.x) / viewport.zoom,
      y: (window.innerHeight / 2 - viewport.y) / viewport.zoom,
    }

    const style = getContainerStyle(template.type, template.provider)

    const newContainer: Node = {
      id: `container-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      type: 'group-container',  // Use new descriptive type name
      position: centerPosition,
      data: {
        label: template.name,
        containerType: template.type,
        provider: template.provider,
        icon: template.icon,
        originalIcon: template.icon, // Store original for restore functionality
        description: template.description,
        bgColor: style.bgColor,
        textColor: style.textColor,
        borderColor: style.borderColor,
        borderStyle: style.borderStyle || 'solid',
        isDropTarget: false,
        layerId: activeLayerId,
      },
      width: template.size.width,
      height: template.size.height,
      style: {
        zIndex: -100  // Root containers start at -100, nested will be higher
      },
      selectable: !readonly,
      deletable: !readonly,
    }

      setNodes((nds) => [...nds, newContainer])
  }, [reactFlow, setNodes, readonly, activeLayerId])

  // ============================================================================
  // TEXT NODE CREATION
  // ============================================================================

  const handleAddTextNode = useCallback(() => {
    const viewport = reactFlow.getViewport()
    const centerPosition = {
      x: (window.innerWidth / 2 - viewport.x) / viewport.zoom,
      y: (window.innerHeight / 2 - viewport.y) / viewport.zoom,
    }

    const newTextNode: Node = {
      id: `text-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      type: 'text',
      position: centerPosition,
      data: {
        label: 'Text',
        layerId: activeLayerId,
        onLabelChange: (nodeId: string, newLabel: string) => {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId ? { ...n, data: { ...n.data, label: newLabel } } : n
            )
          )
        },
      },
      selectable: !readonly,
      deletable: !readonly,
    }

    setNodes((nds) => [...nds, newTextNode])
  }, [reactFlow, setNodes, readonly, activeLayerId])

  // REMOVED: Auto-update z-indices - now only calculated on initial render
  // Z-index is calculated once when containers are created or grouped
  // This prevents re-renders and jumps during drag operations

  // ============================================================================
  // MANUAL GROUPING AND LOCKING
  // ============================================================================


  /**
   * Helper function to get all descendants recursively using React Flow's parentId
   */
  const getDescendants = useCallback((nodeId: string, nodes: Node[]): string[] => {
    const descendants = [nodeId]

    // Find all direct children (nodes where parentId === nodeId)
    const children = nodes.filter(n => n.parentId === nodeId)

    // Recursively get descendants of each child
    children.forEach(child => {
      descendants.push(...getDescendants(child.id, nodes))
    })

    return descendants
  }, [])

  /**
   * Unified Logical Hierarchy Grouping:
   *
   * Simple parent-child linking system:
   * - First selected node becomes parent
   * - Other selected nodes become children
   * - Visual positions stay EXACTLY where they are (no movement)
   * - Children can move anywhere (NO confinement to parent bounds)
   * - When parent moves, children move with it automatically
   * - Supports multi-level nesting: parent → child → grandchild → ...
   * - Works with ANY node type: containers, shapes, icons, etc.
   */
  const handleGroup = useCallback(() => {
    if (selectedNodes.length < 2) {
      modal.alert({ message: 'Please select at least 2 nodes to group', type: 'warning' })
      return
    }

    // First selected node becomes parent, rest become children
    const parentNode = selectedNodes[0]
    const childNodes = selectedNodes.slice(1)

    setNodes(nds => {
      return nds.map(node => {
        // If this node should become a child
        if (childNodes.some(n => n.id === node.id)) {
          // Convert position to relative (maintains visual position, enables coordinated movement)
          const relativePosition = {
            x: node.position.x - parentNode.position.x,
            y: node.position.y - parentNode.position.y,
          }

          return {
            ...node,
            position: relativePosition,      // Relative to parent (for coordinated movement)
            parentId: parentNode.id,         // Set logical parent
            // NO extent - child can move freely anywhere
            // NO expandParent - no auto-resize
            data: {
              ...node.data,
              parentContainer: parentNode.id,  // Backward compat
            }
          }
        }

        return node
      })
    })
  }, [selectedNodes, setNodes, modal])

  /**
   * Arrange helpers — align / distribute / tidy-up. They read measured sizes
   * from React Flow and apply new positions via the tracked `setNodes` so the
   * change is undoable.
   */
  const applyArrange = useCallback(
    (compute: (items: { id: string; x: number; y: number; width: number; height: number }[]) => Map<string, { x: number; y: number }>) => {
      const rfNodes = reactFlow.getNodes()
      const items = selectedNodes.map((sn) => {
        const rf = rfNodes.find((n) => n.id === sn.id) ?? sn
        return {
          id: sn.id,
          x: rf.position.x,
          y: rf.position.y,
          width: (rf as any).measured?.width ?? rf.width ?? 120,
          height: (rf as any).measured?.height ?? rf.height ?? 120,
        }
      })
      if (items.length < 2) return
      const moves = compute(items)
      setNodes((nds) => nds.map((n) => (moves.has(n.id) ? { ...n, position: moves.get(n.id)! } : n)))
    },
    [reactFlow, selectedNodes, setNodes],
  )
  const handleAlign = useCallback((edge: AlignEdge) => applyArrange((it) => alignNodes(it, edge)), [applyArrange])
  const handleDistribute = useCallback((axis: DistributeAxis) => applyArrange((it) => distributeNodes(it, axis)), [applyArrange])
  const handleTidyUp = useCallback(() => applyArrange((it) => tidyUpNodes(it)), [applyArrange])

  /**
   * Ungroup selected nodes - removes parentId and converts positions back to absolute
   * Works with unified logical hierarchy system
   */
  const handleUngroup = useCallback(() => {
    if (selectedNodes.length === 0) return

    setNodes(nds => {
      // Create a map for quick parent lookup
      const nodeMap = new Map(nds.map(n => [n.id, n]))

      // Get IDs of selected nodes
      const selectedNodeIds = new Set(selectedNodes.map(n => n.id))

      return nds.map(node => {
        // Case 1: This node is selected and has a parent - ungroup it
        // Case 2: This node's parent is selected - ungroup it (for "Ungroup All" functionality)
        const shouldUngroup = (
          (selectedNodeIds.has(node.id) && node.parentId) ||  // Selected child
          (node.parentId && selectedNodeIds.has(node.parentId))  // Child of selected parent
        )

        if (shouldUngroup && node.parentId) {
          const parent = nodeMap.get(node.parentId)

          // Convert position from relative to absolute (maintains visual position)
          const absolutePosition = parent ? {
            x: node.position.x + parent.position.x,
            y: node.position.y + parent.position.y,
          } : node.position

          return {
            ...node,
            position: absolutePosition,
            parentId: undefined,         // Remove parent
            extent: undefined,           // Remove constraint (if any)
            expandParent: undefined,     // Remove auto-expand (if any)
            data: {
              ...node.data,
              parentContainer: undefined,  // Remove old field too
            }
          }
        }

        return node
      })
    })
  }, [selectedNodes, setNodes])

  /**
   * Toggle lock/unlock on selected nodes
   */
  const handleToggleLock = useCallback(() => {
    if (selectedNodes.length === 0) return

    const allLocked = selectedNodes.every(n => n.data?.locked === true)
    const newLockedState = !allLocked

    const selectedNodeIds = new Set(selectedNodes.map(n => n.id))

    setNodes(nds =>
      nds.map(node => {
        if (selectedNodeIds.has(node.id)) {
          return {
            ...node,
            data: { ...node.data, locked: newLockedState },
          }
        }
        return node
      })
    )
  }, [selectedNodes, setNodes, readonly])

  /**
   * Toggle auto-resize for a container
   */
  const handleToggleAutoResize = useCallback(() => {
    if (selectedNodes.length !== 1 || selectedNodes[0].type !== 'container') return

    const container = selectedNodes[0]
    const currentAutoResize = container.data?.autoResize === true

    setNodes(nds =>
      nds.map(node => {
        if (node.id === container.id) {
          return {
            ...node,
            data: { ...node.data, autoResize: !currentAutoResize }
          }
        }
        return node
      })
    )
  }, [selectedNodes, setNodes])

  /**
   * Delete selected nodes and edges
   */
  const handleDelete = useCallback(() => {
    if (selectedNodes.length === 0 && selectedEdges.length === 0) return

    // Delete selected nodes and their connected edges
    if (selectedNodes.length > 0) {
      const selectedNodeIds = new Set(selectedNodes.map(n => n.id))
      setNodes(nds => nds.filter(n => !selectedNodeIds.has(n.id)))
      setEdges(eds => eds.filter(e => !selectedNodeIds.has(e.source) && !selectedNodeIds.has(e.target)))
    }

    // Delete selected edges
    if (selectedEdges.length > 0) {
      const selectedEdgeIds = new Set(selectedEdges.map(e => e.id))
      setEdges(eds => eds.filter(e => !selectedEdgeIds.has(e.id)))
    }
  }, [selectedNodes, selectedEdges, setNodes, setEdges])

  /**
   * Accessibility: keyboard nudge. When the canvas region is focused, arrow keys
   * move the selected node(s) by the grid step (Shift = 1px fine nudge). Each
   * nudge goes through the tracked `setNodes` so it is a single undo step. Inert
   * in readonly mode or when nothing is selected.
   */
  const onCanvasKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (readonly) return
      // Don't hijack arrows while editing text inside a node (inline label, etc.)
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }
      const nudge = computeNudge(e.key, e.shiftKey, gridSize)
      if (!nudge) return
      if (!selectedNodes.some((n) => !(n.data as any)?.locked)) return
      e.preventDefault()
      const movable = new Set(
        selectedNodes.filter((n) => !(n.data as any)?.locked).map((n) => n.id),
      )
      setNodes((nds) =>
        nds.map((n) =>
          movable.has(n.id)
            ? { ...n, position: { x: n.position.x + nudge.dx, y: n.position.y + nudge.dy } }
            : n,
        ),
      )
    },
    [readonly, gridSize, selectedNodes, setNodes],
  )

  // ============================================================================
  // Z-ORDER (front / back / forward / backward)
  // ============================================================================
  // React Flow v12 honors `node.zIndex` for stacking. We compute new z-index
  // values across ALL nodes (missing zIndex defaults to 0) and apply via the
  // tracked `setNodes` so each change is a single undo step.

  const handleBringToFront = useCallback(() => {
    if (selectedNodes.length === 0) return
    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    setNodes((nds) => {
      const maxZ = nds.reduce((m, n) => Math.max(m, (n.zIndex ?? 0)), 0)
      // Preserve relative order of the selected nodes as they stack above the rest.
      const orderedSelected = nds.filter((n) => selectedIds.has(n.id))
      const rank = new Map(orderedSelected.map((n, i) => [n.id, i]))
      return nds.map((n) =>
        selectedIds.has(n.id) ? { ...n, zIndex: maxZ + 1 + (rank.get(n.id) ?? 0) } : n,
      )
    })
  }, [selectedNodes, setNodes])

  const handleSendToBack = useCallback(() => {
    if (selectedNodes.length === 0) return
    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    setNodes((nds) => {
      const minZ = nds.reduce((m, n) => Math.min(m, (n.zIndex ?? 0)), 0)
      const orderedSelected = nds.filter((n) => selectedIds.has(n.id))
      const rank = new Map(orderedSelected.map((n, i) => [n.id, i]))
      // Lowest-ranked selected node ends up furthest back.
      const count = orderedSelected.length
      return nds.map((n) =>
        selectedIds.has(n.id)
          ? { ...n, zIndex: minZ - count + (rank.get(n.id) ?? 0) }
          : n,
      )
    })
  }, [selectedNodes, setNodes])

  const handleBringForward = useCallback(() => {
    if (selectedNodes.length === 0) return
    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    setNodes((nds) =>
      nds.map((n) =>
        selectedIds.has(n.id) ? { ...n, zIndex: (n.zIndex ?? 0) + 1 } : n,
      ),
    )
  }, [selectedNodes, setNodes])

  const handleSendBackward = useCallback(() => {
    if (selectedNodes.length === 0) return
    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    setNodes((nds) =>
      nds.map((n) =>
        selectedIds.has(n.id) ? { ...n, zIndex: (n.zIndex ?? 0) - 1 } : n,
      ),
    )
  }, [selectedNodes, setNodes])

  // ============================================================================
  // COPY/PASTE/DUPLICATE
  // ============================================================================

  /**
   * Copy selected nodes to clipboard (Ctrl+C)
   */
  const handleCopy = useCallback(() => {
    if (selectedNodes.length === 0) {
      console.log('📋 Nothing to copy - no nodes selected')
      return
    }

    clipboardService.copy(selectedNodes, edges)
  }, [selectedNodes, edges, clipboardService])

  /**
   * Paste nodes from clipboard with offset (Ctrl+V)
   */
  const handlePaste = useCallback(() => {
    const result = clipboardService.paste({ x: 50, y: 50 })

    if (!result) {
      console.log('📋 Nothing to paste - clipboard is empty')
      return
    }

    // Deselect all current nodes
    setNodes(nds => nds.map(n => ({ ...n, selected: false })))
    setEdges(eds => eds.map(e => ({ ...e, selected: false })))

    // Add pasted nodes and edges
    setNodes(nds => [...nds, ...result.nodes])
    setEdges(eds => [...eds, ...result.edges])

    console.log('📋 Pasted:', {
      nodes: result.nodes.length,
      edges: result.edges.length
    })
  }, [clipboardService, setNodes, setEdges])

  /**
   * Duplicate selected nodes in place (Ctrl+D)
   */
  const handleDuplicate = useCallback(() => {
    if (selectedNodes.length === 0) {
      console.log('✨ Nothing to duplicate - no nodes selected')
      return
    }

    const result = duplicateNodes(selectedNodes, edges, { x: 30, y: 30 })

    // Deselect all current nodes
    setNodes(nds => nds.map(n => ({ ...n, selected: false })))
    setEdges(eds => eds.map(e => ({ ...e, selected: false })))

    // Add duplicated nodes and edges
    setNodes(nds => [...nds, ...result.nodes])
    setEdges(eds => [...eds, ...result.edges])

    console.log('✨ Duplicated:', {
      nodes: result.nodes.length,
      edges: result.edges.length
    })
  }, [selectedNodes, edges, setNodes, setEdges])

  /**
   * Cut selected nodes (Ctrl+X) - Copy then delete
   */
  const handleCut = useCallback(() => {
    if (selectedNodes.length === 0) {
      console.log('✂️ Nothing to cut - no nodes selected')
      return
    }

    // First copy to clipboard
    clipboardService.copy(selectedNodes, edges)

    // Then delete
    handleDelete()

    console.log('✂️ Cut to clipboard:', selectedNodes.length, 'nodes')
  }, [selectedNodes, edges, clipboardService, handleDelete])

  /**
   * Copy the visual style of the first selected node into the style buffer
   * (Ctrl/Cmd+Alt+C).
   */
  const handleCopyStyle = useCallback(() => {
    if (selectedNodes.length === 0) {
      console.log('🎨 Nothing to copy style from - no nodes selected')
      return
    }
    styleClipboard.copyStyle(selectedNodes[0].data as Record<string, unknown>)
    setStyleBufferVersion((v) => v + 1)
  }, [selectedNodes, styleClipboard])

  /**
   * Merge the buffered style into ALL selected nodes (Ctrl/Cmd+Alt+V).
   * Uses the tracked `setNodes` so it lands in undo history as one step.
   */
  const handlePasteStyle = useCallback(() => {
    const style = styleClipboard.getStyle()
    if (!style) {
      console.log('🎨 Nothing to paste - style buffer is empty')
      return
    }
    if (selectedNodes.length === 0) {
      console.log('🎨 Nothing to paste style onto - no nodes selected')
      return
    }

    const selectedNodeIds = new Set(selectedNodes.map((n) => n.id))
    setNodes((nds) =>
      nds.map((n) =>
        selectedNodeIds.has(n.id)
          ? { ...n, data: { ...n.data, ...style } }
          : n,
      ),
    )
    console.log('🎨 Pasted style onto', selectedNodeIds.size, 'node(s)')
  }, [selectedNodes, styleClipboard, setNodes])

  /**
   * Eyedropper: pick a color from anywhere on screen and apply it as `bgColor`
   * to all selected nodes (tracked → undoable). No-ops with a hint when the
   * native EyeDropper API is unavailable (e.g. Firefox/Safari).
   */
  const handleEyedropper = useCallback(async () => {
    const EyeDropperCtor = (window as any).EyeDropper
    if (typeof EyeDropperCtor !== 'function') {
      console.log(
        '🎨 Eyedropper unavailable: window.EyeDropper is not supported in this browser',
      )
      return
    }
    if (selectedNodes.length === 0) {
      console.log('🎨 Eyedropper: no nodes selected to apply the picked color to')
      return
    }

    try {
      const result = await new EyeDropperCtor().open()
      const sRGBHex: string | undefined = result?.sRGBHex
      if (!sRGBHex) return

      const selectedNodeIds = new Set(selectedNodes.map((n) => n.id))
      setNodes((nds) =>
        nds.map((n) =>
          selectedNodeIds.has(n.id)
            ? { ...n, data: { ...n.data, bgColor: sRGBHex } }
            : n,
        ),
      )
      console.log('🎨 Eyedropper applied', sRGBHex, 'to', selectedNodeIds.size, 'node(s)')
    } catch {
      // User dismissed the eyedropper (Escape) — nothing to do.
    }
  }, [selectedNodes, setNodes])

  /**
   * Context menu handlers
   */
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    setContextMenu({ x: event.clientX, y: event.clientY })
  }, [])

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  // ============================================================================
  // REMOVED: Auto-magic drag-to-parent and auto-resize
  // ============================================================================
  // REMOVED: handleNodeDrag - no more auto drop target detection during drag
  // REMOVED: handleNodeDragStop - no more auto-parenting on drop
  // REMOVED: Auto-resize effect - containers don't auto-resize anymore
  //
  // Users now manually group/ungroup using:
  // - Right-click context menu
  // - Toolbar buttons (Group/Ungroup)
  // - Keyboard shortcuts (Ctrl+G / Ctrl+Shift+G)
  //
  // Optional auto-resize can be enabled per-container via context menu

  // ============================================================================
  // DRAG AND DROP
  // ============================================================================

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()

      // Check for icon data
      const iconData = event.dataTransfer.getData('application/reactflow-icon')
      // Check for shape data
      const shapeData = event.dataTransfer.getData('application/reactflow-shape')

      if (!iconData && !shapeData) return

      try {
        // Get the ReactFlow bounds and calculate position
        const reactFlowBounds = (event.target as HTMLElement).closest('.react-flow')?.getBoundingClientRect()
        if (!reactFlowBounds) return

        // Convert screen position to flow position (accounts for zoom/pan)
        const flowPosition = reactFlow.screenToFlowPosition({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        })

        // Center the node on cursor (nodes are 120x120, so offset by 60 in flow coordinates)
        const position = {
          x: flowPosition.x - 60,
          y: flowPosition.y - 60,
        }

        if (iconData) {
          const icon: IconDefinition = JSON.parse(iconData)
          handleAddNode('component' as NodeType, icon, undefined, undefined, position)
        } else if (shapeData) {
          const shape = JSON.parse(shapeData)
          handleAddNode('component' as NodeType, undefined, shape, undefined, position)
        }
      } catch (e) {
        console.error('Failed to parse dropped icon/shape:', e)
      }
    },
    [reactFlow, handleAddNode]
  )

  // ============================================================================
  // UNDO/REDO
  // ============================================================================

  const handleUndo = useCallback(() => {
    isUndoRedoRef.current = true
    store.temporal.getState().undo()
    setTimeout(() => { isUndoRedoRef.current = false }, 200)
  }, [store])

  const handleRedo = useCallback(() => {
    isUndoRedoRef.current = true
    store.temporal.getState().redo()
    setTimeout(() => { isUndoRedoRef.current = false }, 200)
  }, [store])


  // Load a parsed Mermaid diagram into the editor: parse DiagramData → React
  // Flow, run auto-layout (Mermaid has no positions), then commit via the
  // tracked setters so it lands as a single undoable step.
  const handleMermaidImport = useCallback((imported: DiagramData) => {
    if (readonly) return
    const parsed = parseDiagramData(imported)
    let { nodes: importedNodes, edges: importedEdges } = parsed
    importedEdges = importedEdges.map((edge) => ({
      ...edge,
      type: edge.type === 'smart' ? 'smart' : 'custom',
    }))
    // Sequence + Gantt are pre-positioned (time/date-ordered) — never dagre them.
    if (imported.diagramType !== 'sequence' && imported.diagramType !== 'gantt') {
      const direction = imported.layout?.direction || getRecommendedDirection(imported.diagramType)
      importedNodes = applyAutoLayout(importedNodes, importedEdges, {
        algorithm: imported.layout?.algorithm || 'dagre',
        direction,
        spacing: imported.layout?.spacing,
      })
    }
    setNodes(importedNodes)
    setEdges(importedEdges)
    setTimeout(() => {
      reactFlow.fitView({ padding: 0.15, duration: 400 })
    }, 100)
  }, [readonly, setNodes, setEdges, reactFlow])

  // draw.io / mxGraph XML import. The importer only sets `layout` when the XML
  // had no geometry — so we auto-layout only in that case, otherwise we keep
  // the imported positions.
  const handleDrawioImport = useCallback((imported: DiagramData) => {
    if (readonly) return
    const parsed = parseDiagramData(imported)
    let { nodes: importedNodes, edges: importedEdges } = parsed
    importedEdges = importedEdges.map((edge) => ({
      ...edge,
      type: edge.type === 'smart' ? 'smart' : 'custom',
    }))
    if (imported.layout) {
      const direction = imported.layout.direction || getRecommendedDirection(imported.diagramType)
      importedNodes = applyAutoLayout(importedNodes, importedEdges, {
        algorithm: imported.layout.algorithm || 'dagre',
        direction,
        spacing: imported.layout.spacing,
      })
    }
    setNodes(importedNodes)
    setEdges(importedEdges)
    setTimeout(() => {
      reactFlow.fitView({ padding: 0.15, duration: 400 })
    }, 100)
  }, [readonly, setNodes, setEdges, reactFlow])

  // JSON / file-open import (v1 or v2 DiagramData). Exported JSON carries
  // positions, so we preserve them and only auto-layout a position-less document.
  const handleJsonImport = useCallback((imported: DiagramData) => {
    if (readonly) return
    const parsed = parseDiagramData(imported)
    let { nodes: importedNodes, edges: importedEdges } = parsed
    importedEdges = importedEdges.map((edge) => ({
      ...edge,
      type: edge.type === 'smart' ? 'smart' : 'custom',
    }))
    const hasPositions = importedNodes.some(
      (n) => n.position && (n.position.x !== 0 || n.position.y !== 0)
    )
    if (!hasPositions) {
      const direction = imported.layout?.direction || getRecommendedDirection(imported.diagramType)
      importedNodes = applyAutoLayout(importedNodes, importedEdges, {
        algorithm: imported.layout?.algorithm || 'dagre',
        direction,
        spacing: imported.layout?.spacing,
      })
    }
    setNodes(importedNodes)
    setEdges(importedEdges)
    setTimeout(() => {
      reactFlow.fitView({ padding: 0.15, duration: 400 })
    }, 100)
  }, [readonly, setNodes, setEdges, reactFlow])

  const handleAutoLayout = useCallback(() => {
    console.log(`[DiagramEditor] Auto Layout triggered`)

    // Get current diagram type for recommended direction
    const diagramType = data?.diagramType || 'architecture'
    const direction = data?.layout?.direction || getRecommendedDirection(diagramType)

    // Apply auto-layout with structure-aware sizing
    const layoutedNodes = applyAutoLayout(nodes, edges, {
      algorithm: 'dagre',
      direction,
      spacing: data?.layout?.spacing,
    })

    console.log(`[DiagramEditor] Auto Layout complete: ${layoutedNodes.length} nodes repositioned`)
    setNodes(layoutedNodes)

    // Force React Flow to recalculate edge positions and labels
    // This fixes the issue where edge labels don't reposition after auto-layout
    setTimeout(() => {
      reactFlow.fitView({ padding: 0.1, duration: 400 })
      console.log('[DiagramEditor] Edge positions refreshed')
    }, 100)
  }, [nodes, edges, data, setNodes, reactFlow])
  // ============================================================================
  // EXPORT
  // ============================================================================

  // Open export modal with specific format
  const handleOpenExportModal = useCallback((format: 'png' | 'svg' | 'json') => {
    setExportFormat(format)
    setShowExportModal(true)
  }, [])

  // Copy the diagram to the clipboard as a PNG image (no-ops on unsupported
  // browsers, with a console hint).
  const handleCopyAsImage = useCallback(async () => {
    try {
      await copyDiagramAsImage(nodes, { backgroundColor: 'white' })
    } catch (error) {
      console.error('Failed to copy diagram as image:', error)
      modal.alert({ message: 'Failed to copy diagram as image.', type: 'error' })
    }
  }, [nodes, modal])

  // Handle export with options from modal
  const handleExportWithOptions = useCallback(async (options: ExportOptions) => {
    try {
      const fileName = `${data?.title || 'diagram'}.${options.format}`

      if (options.format === 'json') {
        // JSON export — honor BOTH the export-mode toggle (AI-friendly vs Full)
        // and the action toggle (download vs copy-to-clipboard).
        if (!data) return
        const updatedData = serializeToDiagramData(nodes, edges, data.diagramType)
        const json = options.exportMode === 'ai'
          // AI mode: v2-tagged compact form. A fresh tracker means "no incremental
          // edits", so exportForAI emits the full current diagram in AI shape.
          ? exportDiagramByMode('ai', updatedData, nodes, edges, createEditTracker(), { prettify: true })
          : exportToJSON(updatedData, true)

        if (options.jsonAction === 'copy') {
          try {
            await navigator.clipboard.writeText(json)
            modal.alert({ message: 'Diagram JSON copied to clipboard.', type: 'success' })
          } catch {
            // Clipboard can be blocked (insecure context / permissions) — fall back to a download.
            downloadJSON(json, fileName)
          }
        } else if (onExport) {
          onExport(json)
        } else {
          downloadJSON(json, fileName)
        }
      } else if (options.format === 'png') {
        // Serialize the diagram so the exported PNG embeds re-importable JSON.
        // TODO: wire import path to detect/extract this embedded JSON on file open.
        const diagramJson = data
          ? JSON.stringify(serializeToDiagramData(nodes, edges, data.diagramType))
          : undefined
        // PNG export using React Flow's official method + icon data URI fix
        await exportToPNG(nodes, fileName, {
          backgroundColor: options.backgroundColor,
          padding: options.padding,
          quality: options.quality
        }, edges, diagramJson)
      } else if (options.format === 'svg') {
        // Serialize the diagram so the exported SVG embeds re-importable JSON.
        // TODO: wire import path to detect/extract this embedded JSON on file open.
        const diagramJson = data
          ? JSON.stringify(serializeToDiagramData(nodes, edges, data.diagramType))
          : undefined
        // SVG export using React Flow's official method + icon data URI fix
        await exportToSVG(nodes, fileName, {
          backgroundColor: options.backgroundColor,
          padding: options.padding
        }, edges, diagramJson, options.quality)
      } else if (options.format === 'pdf') {
        // PDF export renders the PNG path, then wraps it in a jsPDF page.
        await exportToPDF(nodes, fileName, {
          backgroundColor: options.backgroundColor,
          padding: options.padding,
          quality: options.quality
        }, edges)
      }
    } catch (error) {
      console.error(`Failed to export ${options.format.toUpperCase()}:`, error)
      modal.alert({
        message: `Failed to export ${options.format.toUpperCase()}. Please try again.`,
        type: 'error'
      })
    }
  }, [data, nodes, edges, onExport])

  // ============================================================================
  // PRESENTATION / FULLSCREEN MODE
  // ============================================================================

  /**
   * Toggle presentation mode. Entering hides the chrome (via the
   * `presentation-mode` class) and requests browser fullscreen on the editor
   * root; exiting restores both. Fullscreen is best-effort — if the API is
   * unavailable or rejects, the chrome-hiding still applies.
   */
  const togglePresentationMode = useCallback(() => {
    const root = editorRootRef.current
    const entering = !isPresentationMode

    if (entering) {
      setIsPresentationMode(true)
      if (root && !document.fullscreenElement && root.requestFullscreen) {
        root.requestFullscreen().catch((err) => {
          console.warn('[DiagramEditor] requestFullscreen failed:', err)
        })
      }
    } else {
      setIsPresentationMode(false)
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
      }
    }
  }, [isPresentationMode])

  /**
   * Explicit exit (floating button + Escape). Always clears presentation mode
   * and exits browser fullscreen if active.
   */
  const exitPresentationMode = useCallback(() => {
    setIsPresentationMode(false)
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {})
    }
  }, [])

  // Sync presentation mode when the user leaves fullscreen via the browser
  // (Esc / F11), and handle Escape to exit even when not in real fullscreen.
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsPresentationMode(false)
      }
    }
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPresentationMode) {
        exitPresentationMode()
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      document.removeEventListener('keydown', onEscape)
    }
  }, [isPresentationMode, exitPresentationMode])

  // ============================================================================
  // KEYBOARD SHORTCUTS
  // ============================================================================

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger shortcuts if user is typing in an input/textarea
    const target = event.target as HTMLElement
    const isTyping = target.tagName === 'INPUT' ||
                     target.tagName === 'TEXTAREA' ||
                     target.isContentEditable

    // Command palette: Cmd/Ctrl+K toggles it (works even while typing elsewhere)
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault()
      setShowCommandPalette((v) => !v)
      return
    }

    // Backspace should NEVER delete shapes - only work in text inputs
    if (event.key === 'Backspace') {
      return // Let inputs handle it, but don't delete shapes
    }

    // For Delete, skip if typing in input
    if (event.key === 'Delete' && isTyping) {
      return // Let the input handle it
    }

    // Undo: Ctrl+Z (or Cmd+Z on Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
      console.log('[Keyboard] Ctrl+Z pressed - triggering undo')
      event.preventDefault()
      handleUndo()
    }
    // Redo: Ctrl+Y or Ctrl+Shift+Z (or Cmd+Y / Cmd+Shift+Z on Mac)
    else if (((event.ctrlKey || event.metaKey) && event.key === 'y') || ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z')) {
      console.log('[Keyboard] Ctrl+Y or Ctrl+Shift+Z pressed - triggering redo')
      event.preventDefault()
      handleRedo()
    }
    // Copy style: Ctrl/Cmd+Alt+C (skip if typing). Checked before plain copy so
    // the Alt modifier routes here instead of falling through to handleCopy.
    else if ((event.ctrlKey || event.metaKey) && event.altKey && (event.key === 'c' || event.code === 'KeyC')) {
      if (!isTyping) {
        event.preventDefault()
        handleCopyStyle()
      }
    }
    // Paste style: Ctrl/Cmd+Alt+V (skip if typing).
    else if ((event.ctrlKey || event.metaKey) && event.altKey && (event.key === 'v' || event.code === 'KeyV')) {
      if (!isTyping) {
        event.preventDefault()
        handlePasteStyle()
      }
    }
    // Copy: Ctrl+C (or Cmd+C on Mac) (skip if typing - let browser handle text copy)
    else if ((event.ctrlKey || event.metaKey) && event.key === 'c' && !event.shiftKey && !event.altKey) {
      if (!isTyping) {
        event.preventDefault()
        handleCopy()
      }
    }
    // Cut: Ctrl+X (or Cmd+X on Mac) (skip if typing - let browser handle text cut)
    else if ((event.ctrlKey || event.metaKey) && event.key === 'x' && !event.shiftKey) {
      if (!isTyping) {
        event.preventDefault()
        handleCut()
      }
    }
    // Paste: Ctrl+V (or Cmd+V on Mac) (skip if typing - let browser handle text paste)
    else if ((event.ctrlKey || event.metaKey) && event.key === 'v' && !event.shiftKey && !event.altKey) {
      if (!isTyping) {
        event.preventDefault()
        handlePaste()
      }
    }
    // Duplicate: Ctrl+D (or Cmd+D on Mac) (skip if typing)
    else if ((event.ctrlKey || event.metaKey) && event.key === 'd' && !event.shiftKey) {
      if (!isTyping) {
        event.preventDefault()
        handleDuplicate()
      }
    }
    // Group: Ctrl+G (or Cmd+G on Mac) (skip if typing)
    else if ((event.ctrlKey || event.metaKey) && event.key === 'g' && !event.shiftKey) {
      if (!isTyping) {
        event.preventDefault()
        if (selectedNodes.length >= 2) {
          handleGroup()
        }
      }
    }
    // Ungroup: Ctrl+Shift+G (or Cmd+Shift+G on Mac) (skip if typing)
    else if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'g') {
      if (!isTyping) {
        event.preventDefault()
        // Can ungroup any selected nodes that have group metadata
        if (selectedNodes.length > 0) {
          handleUngroup()
        }
      }
    }
    // Lock/Unlock: Ctrl+L (or Cmd+L on Mac) (skip if typing)
    else if ((event.ctrlKey || event.metaKey) && event.key === 'l') {
      if (!isTyping) {
        event.preventDefault()
        if (selectedNodes.length > 0) {
          handleToggleLock()
        }
      }
    }
    // Z-order: Bring to front (Ctrl+Shift+]) / Bring forward (Ctrl+]) (skip if typing)
    else if ((event.ctrlKey || event.metaKey) && event.key === ']') {
      if (!isTyping && selectedNodes.length > 0) {
        event.preventDefault()
        if (event.shiftKey) handleBringToFront()
        else handleBringForward()
      }
    }
    // Z-order: Send to back (Ctrl+Shift+[) / Send backward (Ctrl+[) (skip if typing)
    else if ((event.ctrlKey || event.metaKey) && event.key === '[') {
      if (!isTyping && selectedNodes.length > 0) {
        event.preventDefault()
        if (event.shiftKey) handleSendToBack()
        else handleSendBackward()
      }
    }
    // Delete: Only Delete key (Backspace never deletes shapes)
    else if (event.key === 'Delete') {
      if ((selectedNodes.length > 0 || selectedEdges.length > 0) && !readonly) {
        event.preventDefault()
        handleDelete()
      }
    }
  }, [handleUndo, handleRedo, handleCopy, handleCut, handlePaste, handleCopyStyle, handlePasteStyle, handleDuplicate, handleGroup, handleUngroup, handleToggleLock, handleDelete, handleBringToFront, handleBringForward, handleSendBackward, handleSendToBack, selectedNodes, selectedEdges, readonly])

  // Attach keyboard handler globally
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // ============================================================================
  // COMMAND PALETTE
  // ============================================================================

  const hasSelection = selectedNodes.length > 0
  const canGroupSelection = selectedNodes.length >= 2
  // `styleBufferVersion` bumps whenever the style buffer is written, forcing this
  // (and the commands useMemo) to re-evaluate `hasStyle()` so "Paste style"
  // enables/disables correctly.
  const hasStyleBuffer = useMemo(
    () => styleClipboard.hasStyle(),
    [styleClipboard, styleBufferVersion],
  )

  // ============================================================================
  // LAYERS — derived render set + mutation handlers
  // ============================================================================

  // Quick lookup of layer state by id. Elements with an unknown/absent layerId
  // are treated as belonging to the first (back-most) layer.
  const layerById = useMemo(() => {
    const m = new Map<string, DiagramLayer>()
    layers.forEach((l) => m.set(l.id, l))
    return m
  }, [layers])

  const resolveLayer = useCallback(
    (layerId: unknown): DiagramLayer | undefined => {
      const id = typeof layerId === 'string' ? layerId : undefined
      return (id && layerById.get(id)) || layers[0]
    },
    [layerById, layers],
  )

  // Only render elements on visible layers; mark elements on locked layers as
  // non-selectable + non-draggable. The full set stays in the store untouched.
  const renderNodes = useMemo(() => {
    return nodes
      .filter((n) => resolveLayer((n.data as any)?.layerId)?.visible !== false)
      .map((n) => {
        const layer = resolveLayer((n.data as any)?.layerId)
        if (layer?.locked) {
          return { ...n, selectable: false, draggable: false }
        }
        return n
      })
  }, [nodes, resolveLayer])

  const renderEdges = useMemo(() => {
    return edges.filter(
      (e) => resolveLayer((e.data as any)?.layerId)?.visible !== false,
    )
  }, [edges, resolveLayer])

  const handleLayerAdd = useCallback(() => {
    const id = `layer-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    setLayers((prev) => {
      const next = [
        ...prev,
        { id, name: `Layer ${prev.length + 1}`, visible: true, locked: false },
      ]
      return next
    })
    setActiveLayerId(id)
  }, [])

  const handleLayerDelete = useCallback(
    (id: string) => {
      setLayers((prev) => {
        if (prev.length <= 1) return prev
        const next = prev.filter((l) => l.id !== id)
        // Reassign any elements on the deleted layer to the new first layer.
        const fallback = next[0].id
        setNodes((nds) =>
          nds.map((n) =>
            (n.data as any)?.layerId === id
              ? { ...n, data: { ...n.data, layerId: fallback } }
              : n,
          ),
        )
        setEdges((eds) =>
          eds.map((e) =>
            (e.data as any)?.layerId === id
              ? { ...e, data: { ...e.data, layerId: fallback } }
              : e,
          ),
        )
        setActiveLayerId((cur) => (cur === id ? fallback : cur))
        return next
      })
    },
    [setNodes, setEdges],
  )

  const handleLayerToggleVisible = useCallback((id: string) => {
    setLayers((prev) =>
      prev.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l)),
    )
  }, [])

  const handleLayerToggleLocked = useCallback((id: string) => {
    setLayers((prev) =>
      prev.map((l) => (l.id === id ? { ...l, locked: !l.locked } : l)),
    )
  }, [])

  const handleLayerRename = useCallback((id: string, name: string) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, name } : l)))
  }, [])

  const handleLayerMove = useCallback((id: string, direction: 'up' | 'down') => {
    setLayers((prev) => {
      const idx = prev.findIndex((l) => l.id === id)
      if (idx < 0) return prev
      // 'up' = toward front = higher index; 'down' = toward back = lower index.
      const target = direction === 'up' ? idx + 1 : idx - 1
      if (target < 0 || target >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }, [])

  // ============================================================================
  // COMMENTS / ANNOTATION THREADS
  // ============================================================================

  // Persist comment changes immediately (comments live outside the zundo store,
  // so they don't ride along on the node/edge onChange path automatically).
  const emitCommentsChange = useCallback(
    (next: DiagramComment[]) => {
      if (readonly || !onChange || !data) return
      const currentNodes = reactFlow.getNodes()
      const currentEdges = reactFlow.getEdges()
      isLocalChangeRef.current = true
      onChange(
        serializeToDiagramData(currentNodes, currentEdges, data.diagramType, {
          layers: layersRef.current,
          comments: next,
          ...gridStateRef.current,
        }),
      )
    },
    [readonly, onChange, data, reactFlow],
  )

  const handleCommentAdd = useCallback(
    (text: string) => {
      // Anchor a brand-new comment to the current selection's first node when
      // one is selected; otherwise drop it at the viewport center (flow coords).
      const selectedId = reactFlow.getNodes().find((n) => n.selected)?.id
      let anchor: { x: number; y: number } | undefined
      let nodeId: string | undefined = selectedId
      if (!nodeId) {
        // Center of the current viewport in flow coordinates.
        const vp = reactFlow.getViewport()
        const el = editorRootRef.current?.querySelector('.diagram-canvas-region')
        const rect = el?.getBoundingClientRect()
        const cx = rect ? rect.width / 2 : 0
        const cy = rect ? rect.height / 2 : 0
        anchor = { x: (cx - vp.x) / vp.zoom, y: (cy - vp.y) / vp.zoom }
      }
      const comment = createComment({ author: 'You', text, anchor, nodeId })
      setComments((prev) => {
        const next = addCommentToList(prev, comment)
        emitCommentsChange(next)
        return next
      })
      setActiveCommentId(comment.id)
    },
    [reactFlow, emitCommentsChange],
  )

  const handleCommentToggleResolved = useCallback(
    (id: string) => {
      setComments((prev) => {
        const next = toggleCommentResolved(prev, id)
        emitCommentsChange(next)
        return next
      })
    },
    [emitCommentsChange],
  )

  const handleCommentDelete = useCallback(
    (id: string) => {
      setComments((prev) => {
        const next = deleteCommentFromList(prev, id)
        emitCommentsChange(next)
        return next
      })
      setActiveCommentId((cur) => (cur === id ? null : cur))
    },
    [emitCommentsChange],
  )

  const handleCommentReply = useCallback(
    (id: string, text: string) => {
      setComments((prev) => {
        const next = addReplyToList(prev, id, { author: 'You', text })
        emitCommentsChange(next)
        return next
      })
    },
    [emitCommentsChange],
  )

  const handleCommentFocus = useCallback(
    (id: string) => {
      setActiveCommentId(id)
      // Pan to the comment's node anchor if it has one.
      setShowComments(true)
      const comment = commentsRef.current.find((c) => c.id === id)
      if (comment?.nodeId) {
        reactFlow.fitView({ nodes: [{ id: comment.nodeId }], padding: 0.4, duration: 200 })
      }
    },
    [reactFlow],
  )

  // Snap-to-grid handlers. The grid size cycles through a few sensible presets
  // (8 / 16 / 24 / 32) so it stays configurable without a free-text prompt.
  const GRID_SIZE_PRESETS = useMemo(() => [8, 16, 24, 32], [])
  const handleToggleSnapToGrid = useCallback(() => {
    setSnapToGrid((v) => !v)
  }, [])
  const handleCycleGridSize = useCallback(() => {
    setGridSize((current) => {
      const idx = GRID_SIZE_PRESETS.indexOf(normalizeGridSize(current))
      const next = GRID_SIZE_PRESETS[(idx + 1) % GRID_SIZE_PRESETS.length]
      return next
    })
  }, [GRID_SIZE_PRESETS])

  /**
   * Zoom-to-selection: fit the viewport to the currently selected nodes (or the
   * whole diagram when nothing is selected). Uses React Flow's fitView with the
   * selected node ids so the camera frames just the selection.
   */
  const handleZoomToSelection = useCallback(() => {
    const selectedIds = reactFlow
      .getNodes()
      .filter((n) => n.selected)
      .map((n) => ({ id: n.id }))
    reactFlow.fitView(
      selectedIds.length > 0
        ? { nodes: selectedIds, padding: 0.2, duration: 300 }
        : { padding: 0.2, duration: 300 },
    )
  }, [reactFlow])

  const commands = useMemo<Command[]>(
    () => [
      // --- Edit -----------------------------------------------------------
      {
        id: 'undo',
        label: 'Undo',
        group: 'Edit',
        shortcut: 'Ctrl+Z',
        icon: <Undo2 size={16} />,
        disabled: !canUndo,
        run: handleUndo,
      },
      {
        id: 'redo',
        label: 'Redo',
        group: 'Edit',
        shortcut: 'Ctrl+Y',
        icon: <Redo2 size={16} />,
        disabled: !canRedo,
        run: handleRedo,
      },
      {
        id: 'cut',
        label: 'Cut',
        group: 'Edit',
        keywords: 'remove move',
        shortcut: 'Ctrl+X',
        icon: <Scissors size={16} />,
        disabled: !hasSelection,
        run: handleCut,
      },
      {
        id: 'copy',
        label: 'Copy',
        group: 'Edit',
        keywords: 'clipboard',
        shortcut: 'Ctrl+C',
        icon: <Copy size={16} />,
        disabled: !hasSelection,
        run: handleCopy,
      },
      {
        id: 'paste',
        label: 'Paste',
        group: 'Edit',
        keywords: 'clipboard',
        shortcut: 'Ctrl+V',
        icon: <Clipboard size={16} />,
        run: handlePaste,
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        group: 'Edit',
        keywords: 'clone copy',
        shortcut: 'Ctrl+D',
        icon: <Files size={16} />,
        disabled: !hasSelection,
        run: handleDuplicate,
      },
      {
        id: 'copy-style',
        label: 'Copy style',
        group: 'Edit',
        keywords: 'format painter appearance theme',
        shortcut: 'Ctrl+Alt+C',
        icon: <Paintbrush size={16} />,
        disabled: !hasSelection,
        run: handleCopyStyle,
      },
      {
        id: 'paste-style',
        label: 'Paste style',
        group: 'Edit',
        keywords: 'format painter appearance theme apply',
        shortcut: 'Ctrl+Alt+V',
        icon: <PaintbrushVertical size={16} />,
        disabled: !hasStyleBuffer || !hasSelection,
        run: handlePasteStyle,
      },
      {
        id: 'eyedropper',
        label: 'Pick color (eyedropper)',
        group: 'Edit',
        keywords: 'color picker sample fill bgcolor dropper',
        icon: <Pipette size={16} />,
        disabled: !hasSelection,
        run: handleEyedropper,
      },
      {
        id: 'delete',
        label: 'Delete',
        group: 'Edit',
        keywords: 'remove',
        shortcut: 'Del',
        icon: <Trash2 size={16} />,
        disabled: !hasSelection && selectedEdges.length === 0,
        run: handleDelete,
      },

      // --- Insert ---------------------------------------------------------
      {
        id: 'add-container',
        label: 'Add container…',
        group: 'Insert',
        keywords: 'group box frame template',
        icon: <BoxSelect size={16} />,
        run: () => setShowContainerModal(true),
      },
      {
        id: 'add-text',
        label: 'Add text',
        group: 'Insert',
        keywords: 'label annotation',
        icon: <TypeIcon size={16} />,
        run: handleAddTextNode,
      },
      {
        id: 'import-mermaid',
        label: 'Import from Mermaid…',
        group: 'Insert',
        keywords: 'mermaid flowchart graph text markdown diagram import',
        icon: <Workflow size={16} />,
        run: () => setShowMermaidImport(true),
      },
      {
        id: 'import-drawio',
        label: 'Import from draw.io (XML)…',
        group: 'Insert',
        keywords: 'drawio draw.io mxgraph xml diagrams.net import diagram',
        icon: <Workflow size={16} />,
        run: () => setShowDrawioImport(true),
      },
      {
        id: 'import-json',
        label: 'Import / open JSON…',
        group: 'Insert',
        keywords: 'json import open file load diagramdata paste restore',
        icon: <Workflow size={16} />,
        run: () => setShowJsonImport(true),
      },

      // --- Arrange --------------------------------------------------------
      {
        id: 'group',
        label: 'Group',
        group: 'Arrange',
        keywords: 'combine link parent',
        shortcut: 'Ctrl+G',
        icon: <GroupIcon size={16} />,
        disabled: !canGroupSelection,
        run: handleGroup,
      },
      {
        id: 'ungroup',
        label: 'Ungroup',
        group: 'Arrange',
        keywords: 'split unlink',
        shortcut: 'Ctrl+Shift+G',
        icon: <Ungroup size={16} />,
        disabled: !hasSelection,
        run: handleUngroup,
      },
      {
        id: 'toggle-lock',
        label: 'Lock / Unlock',
        group: 'Arrange',
        keywords: 'freeze pin',
        shortcut: 'Ctrl+L',
        icon: <Lock size={16} />,
        disabled: !hasSelection,
        run: handleToggleLock,
      },
      {
        id: 'auto-layout',
        label: 'Auto layout',
        group: 'Arrange',
        keywords: 'arrange organize dagre tidy',
        icon: <Network size={16} />,
        run: handleAutoLayout,
      },
      {
        id: 'align-left',
        label: 'Align left',
        group: 'Arrange',
        keywords: 'align edge horizontal',
        icon: <AlignHorizontalJustifyStart size={16} />,
        disabled: selectedNodes.length < 2,
        run: () => handleAlign('left'),
      },
      {
        id: 'align-hcenter',
        label: 'Align horizontal center',
        group: 'Arrange',
        keywords: 'align center horizontal middle',
        icon: <AlignHorizontalJustifyCenter size={16} />,
        disabled: selectedNodes.length < 2,
        run: () => handleAlign('hcenter'),
      },
      {
        id: 'align-right',
        label: 'Align right',
        group: 'Arrange',
        keywords: 'align edge horizontal',
        icon: <AlignHorizontalJustifyEnd size={16} />,
        disabled: selectedNodes.length < 2,
        run: () => handleAlign('right'),
      },
      {
        id: 'align-top',
        label: 'Align top',
        group: 'Arrange',
        keywords: 'align edge vertical',
        icon: <AlignVerticalJustifyStart size={16} />,
        disabled: selectedNodes.length < 2,
        run: () => handleAlign('top'),
      },
      {
        id: 'align-vcenter',
        label: 'Align vertical center',
        group: 'Arrange',
        keywords: 'align center vertical middle',
        icon: <AlignVerticalJustifyCenter size={16} />,
        disabled: selectedNodes.length < 2,
        run: () => handleAlign('vcenter'),
      },
      {
        id: 'align-bottom',
        label: 'Align bottom',
        group: 'Arrange',
        keywords: 'align edge vertical',
        icon: <AlignVerticalJustifyEnd size={16} />,
        disabled: selectedNodes.length < 2,
        run: () => handleAlign('bottom'),
      },
      {
        id: 'distribute-horizontal',
        label: 'Distribute horizontally',
        group: 'Arrange',
        keywords: 'space spread even horizontal',
        icon: <AlignHorizontalDistributeCenter size={16} />,
        disabled: selectedNodes.length < 3,
        run: () => handleDistribute('horizontal'),
      },
      {
        id: 'distribute-vertical',
        label: 'Distribute vertically',
        group: 'Arrange',
        keywords: 'space spread even vertical',
        icon: <AlignVerticalDistributeCenter size={16} />,
        disabled: selectedNodes.length < 3,
        run: () => handleDistribute('vertical'),
      },
      {
        id: 'tidy-up',
        label: 'Tidy up',
        group: 'Arrange',
        keywords: 'grid pack arrange clean organize',
        icon: <LayoutGrid size={16} />,
        disabled: selectedNodes.length < 2,
        run: handleTidyUp,
      },
      {
        id: 'bring-to-front',
        label: 'Bring to front',
        group: 'Arrange',
        keywords: 'z-order stack layer top front raise',
        shortcut: 'Ctrl+Shift+]',
        icon: <BringToFront size={16} />,
        disabled: !hasSelection,
        run: handleBringToFront,
      },
      {
        id: 'bring-forward',
        label: 'Bring forward',
        group: 'Arrange',
        keywords: 'z-order stack layer raise up',
        shortcut: 'Ctrl+]',
        icon: <ArrowUp size={16} />,
        disabled: !hasSelection,
        run: handleBringForward,
      },
      {
        id: 'send-backward',
        label: 'Send backward',
        group: 'Arrange',
        keywords: 'z-order stack layer lower down',
        shortcut: 'Ctrl+[',
        icon: <ArrowDown size={16} />,
        disabled: !hasSelection,
        run: handleSendBackward,
      },
      {
        id: 'send-to-back',
        label: 'Send to back',
        group: 'Arrange',
        keywords: 'z-order stack layer bottom back lower',
        shortcut: 'Ctrl+Shift+[',
        icon: <SendToBack size={16} />,
        disabled: !hasSelection,
        run: handleSendToBack,
      },

      // --- View -----------------------------------------------------------
      {
        id: 'zoom-to-selection',
        label: hasSelection ? 'Zoom to selection' : 'Zoom to fit',
        group: 'View',
        keywords: 'zoom selection fit focus frame camera viewport center',
        icon: <Maximize2 size={16} />,
        run: handleZoomToSelection,
      },
      {
        id: 'toggle-grid',
        label: showGrid ? 'Hide grid' : 'Show grid',
        group: 'View',
        keywords: 'background dots',
        icon: <Grid3x3 size={16} />,
        run: () => setShowGrid(!showGrid),
      },
      {
        id: 'toggle-snap-to-grid',
        label: snapToGrid ? 'Disable snap to grid' : 'Enable snap to grid',
        group: 'View',
        keywords: 'snap grid align magnet pixel position drag',
        icon: <Grid3x3 size={16} />,
        run: handleToggleSnapToGrid,
      },
      {
        id: 'cycle-grid-size',
        label: `Grid size: ${gridSize}px (cycle)`,
        group: 'View',
        keywords: 'snap grid size spacing step cycle',
        icon: <Grid3x3 size={16} />,
        run: handleCycleGridSize,
      },
      {
        id: 'toggle-pan-mode',
        label: isPanMode ? 'Switch to select mode' : 'Switch to pan mode',
        group: 'View',
        keywords: 'hand move cursor selection',
        icon: <Hand size={16} />,
        run: () => setIsPanMode(!isPanMode),
      },
      {
        id: 'toggle-dark-mode',
        label: activeTheme === 'dark' ? 'Switch to light mode' : 'Toggle dark mode',
        group: 'View',
        keywords: 'theme appearance light night color scheme',
        icon: activeTheme === 'dark' ? <Sun size={16} /> : <Moon size={16} />,
        run: () => setActiveTheme(t => (t === 'dark' ? 'light' : 'dark')),
      },
      {
        id: 'presentation-mode',
        label: isPresentationMode ? 'Exit presentation mode' : 'Presentation mode (fullscreen)',
        group: 'View',
        keywords: 'fullscreen full screen present slideshow focus hide chrome',
        icon: <Maximize2 size={16} />,
        run: togglePresentationMode,
      },
      {
        id: 'toggle-outline',
        label: showOutline ? 'Hide outline' : 'Toggle outline',
        group: 'View',
        keywords: 'shapes tree list find search navigate elements',
        icon: <ListTree size={16} />,
        run: () => setShowOutline((v) => !v),
      },
      {
        id: 'toggle-layers',
        label: showLayers ? 'Hide layers' : 'Toggle layers',
        group: 'View',
        keywords: 'layer visibility lock z-order stack drawio',
        icon: <LayersIcon size={16} />,
        run: () => setShowLayers((v) => !v),
      },
      {
        id: 'toggle-rulers',
        label: showRulers ? 'Hide rulers' : 'Toggle rulers',
        group: 'View',
        keywords: 'ruler measure ticks guides gridlines scale',
        icon: <Ruler size={16} />,
        run: () => setShowRulers((v) => !v),
      },
      {
        id: 'toggle-comments',
        label: showComments ? 'Hide comments' : 'Toggle comments',
        group: 'View',
        keywords: 'comment annotation thread review feedback note discuss resolve',
        icon: <MessageSquare size={16} />,
        run: () => setShowComments((v) => !v),
      },
      ...(onToggleNodePalette
        ? [
            {
              id: 'toggle-library',
              label: showNodePalette ? 'Hide library' : 'Show library',
              group: 'View',
              keywords: 'palette nodes sidebar',
              icon: <BoxSelect size={16} />,
              run: onToggleNodePalette,
            } as Command,
          ]
        : []),
      ...(onTogglePropertiesPanel
        ? [
            {
              id: 'toggle-properties',
              label: showPropertiesPanel ? 'Hide properties' : 'Show properties',
              group: 'View',
              keywords: 'inspector panel style',
              icon: <BoxSelect size={16} />,
              run: onTogglePropertiesPanel,
            } as Command,
          ]
        : []),

      // --- Export ---------------------------------------------------------
      {
        id: 'export',
        label: 'Export…',
        group: 'Export',
        keywords: 'download save',
        icon: <Download size={16} />,
        run: () => setShowExportModal(true),
      },
      {
        id: 'export-png',
        label: 'Export as PNG',
        group: 'Export',
        keywords: 'download image',
        icon: <Download size={16} />,
        run: () => handleOpenExportModal('png'),
      },
      {
        id: 'export-svg',
        label: 'Export as SVG',
        group: 'Export',
        keywords: 'download vector',
        icon: <Download size={16} />,
        run: () => handleOpenExportModal('svg'),
      },
      {
        id: 'export-json',
        label: 'Export as JSON',
        group: 'Export',
        keywords: 'download data',
        icon: <Download size={16} />,
        run: () => handleOpenExportModal('json'),
      },
      {
        id: 'copy-as-image',
        label: 'Copy as image',
        group: 'Export',
        keywords: 'clipboard png picture screenshot',
        icon: <ImageDown size={16} />,
        run: handleCopyAsImage,
      },
    ],
    [
      canUndo,
      canRedo,
      hasSelection,
      canGroupSelection,
      selectedNodes,
      selectedEdges.length,
      showGrid,
      snapToGrid,
      gridSize,
      handleToggleSnapToGrid,
      handleCycleGridSize,
      handleZoomToSelection,
      isPanMode,
      activeTheme,
      isPresentationMode,
      togglePresentationMode,
      showOutline,
      showRulers,
      showLayers,
      showComments,
      showNodePalette,
      showPropertiesPanel,
      onToggleNodePalette,
      onTogglePropertiesPanel,
      handleUndo,
      handleRedo,
      handleCut,
      handleCopy,
      handlePaste,
      handleCopyStyle,
      handlePasteStyle,
      handleEyedropper,
      hasStyleBuffer,
      handleDuplicate,
      handleDelete,
      handleAddTextNode,
      handleGroup,
      handleUngroup,
      handleToggleLock,
      handleAutoLayout,
      handleAlign,
      handleDistribute,
      handleTidyUp,
      handleBringToFront,
      handleBringForward,
      handleSendBackward,
      handleSendToBack,
      handleOpenExportModal,
      handleCopyAsImage,
    ],
  )

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div
      ref={editorRootRef}
      data-theme={activeTheme}
      className={`diagram-editor-enhanced diagram-editor-${activeTheme} ${isPresentationMode ? 'presentation-mode' : ''} ${className}`}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      {/* Floating "Exit presentation" button — only in presentation mode */}
      {isPresentationMode && (
        <button
          type="button"
          className="btn presentation-exit-btn"
          onClick={exitPresentationMode}
          title="Exit presentation (Esc)"
        >
          <Maximize2 size={16} />
          Exit presentation
        </button>
      )}
      {/* Toolbar */}
      {showToolbar && (
        <Toolbar
          diagramTitle={data?.title}
          diagramType={data?.diagramType}
          onExport={() => setShowExportModal(true)}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          onToggleGrid={() => setShowGrid(!showGrid)}
          showGrid={showGrid}
          onToggleSnapToGrid={handleToggleSnapToGrid}
          snapToGrid={snapToGrid}
          onToggleNodePalette={onToggleNodePalette}
          onTogglePropertiesPanel={onTogglePropertiesPanel}
          showNodePalette={showNodePalette}
          showPropertiesPanel={showPropertiesPanel}
          onTogglePanMode={() => setIsPanMode(!isPanMode)}
          isPanMode={isPanMode}
          onCreateContainer={() => setShowContainerModal(true)}
          onCreateTextNode={handleAddTextNode}
          onGroup={handleGroup}
          onUngroup={handleUngroup}
          onToggleLock={handleToggleLock}
          onAutoLayout={handleAutoLayout}
          canGroup={selectedNodes.length >= 2}
          canUngroup={selectedNodes.length > 0 && selectedNodes.some(n => (n.data as any)?.group?.groupId)}
          hasSelection={selectedNodes.length > 0}
          isLocked={selectedNodes.length > 0 && selectedNodes.every(n => n.data?.locked === true)}
        />
      )}

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden', minHeight: 0 }}>
        {/* Live collaboration badge + peer avatars (only when connected) */}
        {collab && collabResult.connected && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <PresenceAvatars peers={collabPeers} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                background: 'rgba(34,197,94,0.12)',
                color: '#16a34a',
                border: '1px solid rgba(34,197,94,0.35)',
                pointerEvents: 'none',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
              Live: {collab.room}
            </div>
          </div>
        )}

        {/* Outline / shapes-tree panel */}
        {showOutline && (
          <OutlinePanel
            nodes={nodes}
            edges={edges}
            reactFlow={reactFlow as any}
            onSelectNode={(nodeId) => {
              setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === nodeId })))
            }}
            onSelectEdge={(edgeId) => {
              setEdges((eds) => eds.map((e) => ({ ...e, selected: e.id === edgeId })))
            }}
            onClose={() => setShowOutline(false)}
          />
        )}

        {/* Layers panel (draw.io-style) */}
        {showLayers && (
          <LayersPanel
            layers={layers}
            activeLayerId={activeLayerId}
            onSetActive={setActiveLayerId}
            onToggleVisible={handleLayerToggleVisible}
            onToggleLocked={handleLayerToggleLocked}
            onRename={handleLayerRename}
            onMove={handleLayerMove}
            onAdd={handleLayerAdd}
            onDelete={handleLayerDelete}
            onClose={() => setShowLayers(false)}
          />
        )}

        {/* Comments panel (annotation threads) */}
        {showComments && (
          <CommentsPanel
            comments={comments}
            activeCommentId={activeCommentId}
            onFocus={handleCommentFocus}
            onAdd={handleCommentAdd}
            onToggleResolved={handleCommentToggleResolved}
            onDelete={handleCommentDelete}
            onReply={handleCommentReply}
            onClose={() => setShowComments(false)}
          />
        )}

        {/* Node Palette */}
        {showNodePalette && data && (
          <NodePalette
            diagramType={data.diagramType}
            onAddNode={handleAddNode}
            onClose={onToggleNodePalette}
          />
        )}

        {/* Canvas */}
        <div
          className="diagram-canvas-region"
          style={{ flex: 1, position: 'relative', minHeight: 0, zIndex: 1 }}
          tabIndex={0}
          role="application"
          aria-label="Diagram canvas. Use arrow keys to move the selected element; hold Shift for fine adjustment."
          onDrop={onDrop}
          onDragOver={onDragOver}
          onContextMenu={handleContextMenu}
          onKeyDown={onCanvasKeyDown}
        >
          <ReactFlow
            colorMode={activeTheme}
            proOptions={{ hideAttribution: true }}
            nodes={renderNodes}
            edges={renderEdges}
            onNodesChange={handleNodesChangeCallback}
            onEdgesChange={handleEdgesChangeCallback}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onReconnect={onReconnect}
            onReconnectStart={onReconnectStart}
            onReconnectEnd={onReconnectEnd}
            reconnectRadius={20}
            onSelectionChange={onSelectionChange}
            onPaneMouseMove={collab ? onPaneMouseMove : undefined}
            onMove={(_, vp) => setZoom(vp.zoom)}
            onMoveEnd={(_, vp) => onViewportChange?.(vp)}
            onNodeClick={handleNodeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodesDraggable={!readonly}
            nodesConnectable={!readonly}
            nodesFocusable
            edgesFocusable
            elementsSelectable={!readonly}
            deleteKeyCode={null} // Handled by our keyboard shortcuts
            minZoom={0.1}
            maxZoom={4}
            snapToGrid={snapToGrid}
            snapGrid={[gridSize, gridSize]}
            defaultViewport={defaultViewport}
            fitView={!defaultViewport}
            defaultEdgeOptions={{
              type: 'custom',
              animated: false,
            }}
            // Pan Mode vs Selection Mode
            // Mouse buttons: 0=left, 1=middle, 2=right
            panOnDrag={isPanMode ? [0, 1] : [1]} // Pan mode: left+middle | Selection mode: middle only
            selectionOnDrag={!isPanMode} // Box selection only in selection mode
            selectionKeyCode={null} // Don't require Shift for box selection
            multiSelectionKeyCode={['Meta', 'Control', 'Shift']} // Hold Ctrl/Cmd/Shift to add to selection
            // Performance optimizations
            autoPanOnNodeDrag={true}
            autoPanOnConnect={true}
            zoomOnScroll={true}
            preventScrolling={true}
            nodeOrigin={[0.5, 0.5]}
            style={{ width: '100%', height: '100%', cursor: isPanMode ? 'grab' : 'default' }}
          >
            {showGrid && <Background />}
            {showRulers && <RulerOverlay />}
            {showControls && <Controls />}
            {showMinimap && (
              <MiniMap
                nodeStrokeWidth={2}
                zoomable
                pannable
                // White-filled nodes are invisible on the default minimap; map them to a
                // neutral slate so the minimap is actually readable, and keep real colours.
                nodeColor={(n) => {
                  const bg = (n.data as any)?.bgColor
                  return bg && bg !== '#ffffff' && bg !== 'transparent' ? bg : '#cbd3de'
                }}
                nodeStrokeColor="#94a3b8"
                maskColor="rgba(14, 19, 32, 0.06)"
                style={{ background: '#f6f8fb' }}
              />
            )}
            {/* Zoom-percentage readout + zoom-to-selection action */}
            <div className="zoom-indicator">
              <button
                type="button"
                className="zoom-indicator-btn"
                title={selectedNodes.length > 0 ? 'Zoom to selection' : 'Zoom to fit'}
                aria-label={selectedNodes.length > 0 ? 'Zoom to selection' : 'Zoom to fit'}
                onClick={handleZoomToSelection}
              >
                <Maximize2 size={14} />
              </button>
              <span className="zoom-indicator-value" aria-live="polite">
                {formatZoomPercent(zoom)}
              </span>
            </div>
            <HelperLines horizontal={helperLineHorizontal} vertical={helperLineVertical} />
            {/* Comment pins (flow-coord overlay). Shown when the comments panel
                is open or there are existing threads to surface. */}
            {(showComments || comments.length > 0) && (
              <CommentLayer
                comments={comments}
                activeCommentId={activeCommentId}
                onPinClick={handleCommentFocus}
              />
            )}
            {/* Remote peer cursors (flow-coord overlay). Inert when collab off. */}
            {collab && <PresenceCursors peers={collabPeers} />}
          </ReactFlow>
        </div>

        {/* Properties Panel */}
        {showPropertiesPanel && (
          <PropertiesPanelEnhanced
            selectedNodes={selectedNodes}
            selectedEdges={selectedEdges}
            allNodes={nodes}
            onNodeUpdate={handleNodeUpdate}
            onNodesUpdate={handleNodesUpdate}
            onEdgeUpdate={handleEdgeUpdate}
            onSelectNode={(nodeId) => {
              // Deselect all, select the one
              setNodes(nds => nds.map(n => ({ ...n, selected: n.id === nodeId })))
            }}
            onGroupNodes={handleGroup}
            onUngroupNodes={handleUngroup}
            onClose={onTogglePropertiesPanel}
          />
        )}
      </div>

      {/* Mobile Bottom Toolbar (only shows on mobile <768px) */}
      <MobileToolbar
        onOpenLibrary={onToggleNodePalette}
        onOpenProperties={onTogglePropertiesPanel}
        onExport={() => handleOpenExportModal('json')}
        onShowMore={() => setShowCommandPalette(true)}
      />

      {/* Container Template Selector Modal */}
      <ContainerTemplateSelector
        isOpen={showContainerModal}
        onClose={() => setShowContainerModal(false)}
        onCreate={handleCreateContainer}
      />

      {/* Export Options Modal */}
      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExportWithOptions}
        defaultFormat={exportFormat}
      />

      {/* Mermaid Import Modal */}
      <MermaidImportModal
        isOpen={showMermaidImport}
        onClose={() => setShowMermaidImport(false)}
        onImport={handleMermaidImport}
      />

      {/* draw.io / mxGraph XML Import Modal */}
      <JSONImportModal
        isOpen={showJsonImport}
        onClose={() => setShowJsonImport(false)}
        onImport={handleJsonImport}
      />

      <DrawioImportModal
        isOpen={showDrawioImport}
        onClose={() => setShowDrawioImport(false)}
        onImport={handleDrawioImport}
      />

      {/* Command Palette (Cmd/Ctrl+K) */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        commands={commands}
      />

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          position={contextMenu}
          items={buildContextMenuItems(selectedNodes, {
            onGroup: handleGroup,
            onUngroup: handleUngroup,
            onLock: handleToggleLock,
            onUnlock: handleToggleLock,
            onDelete: handleDelete,
            onDuplicate: handleDuplicate,
            onPaste: handlePaste,
            canPaste: clipboardService.hasData(),
            onToggleAutoResize: handleToggleAutoResize,
            onAlign: handleAlign,
            onDistribute: handleDistribute,
            onTidyUp: handleTidyUp,
            onBringToFront: handleBringToFront,
            onBringForward: handleBringForward,
            onSendBackward: handleSendBackward,
            onSendToBack: handleSendToBack
          })}
          onClose={handleCloseContextMenu}
        />
      )}
    </div>
  )
}

function buildNodePositions(flowNodes: Node[]): NodePosition[] {
  if (!flowNodes.length) {
    return []
  }

  return flowNodes.map((node) => ({
    id: node.id,
    x: typeof (node as any).positionAbsolute?.x === 'number'
      ? (node as any).positionAbsolute.x
      : node.position.x,
    y: typeof (node as any).positionAbsolute?.y === 'number'
      ? (node as any).positionAbsolute.y
      : node.position.y,
  }))
}

function buildProcessedEdgeMap(edges: Edge[], nodePositions: NodePosition[]): Map<string, DiagramEdge> {
  if (!edges.length) {
    return new Map()
  }

  const diagramEdges: DiagramEdge[] = edges.map((edge) => {
    const label = typeof edge.label === 'string' ? edge.label : undefined
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle ?? undefined,
      targetHandle: edge.targetHandle ?? undefined,
      label,
      data: edge.data ? { ...edge.data } : {},
    }
  })

  const edgesWithHandles = autoAssignHandles(diagramEdges, nodePositions)
  const edgesWithOffsets = assignEdgeOffsets(edgesWithHandles, {
    nodePositions,
    enableFanIn: true,
    enableFanOut: true,
    enableBidirectional: true,
    enableDistanceScaling: true,
  })

  const processedMap = new Map<string, DiagramEdge>()
  edgesWithOffsets.forEach((edge) => {
    if (!edge.id) {
      return
    }
    processedMap.set(edge.id, edge)
  })

  return processedMap
}

// ============================================================================
// HELPERS
// ============================================================================

function getIconForNodeType(nodeType: NodeType): string {
  const iconMap: Record<string, string> = {
    component: '⚛️',
    service: '⚙️',
    database: '🗄️',
    api: '🚪',
    cloud: '☁️',
    storage: '📦',
    queue: '📨',
    function: 'λ',
    loadbalancer: '⚖️',
    server: '🖥️',
    screen: '📱',
    actor: '👤',
    action: '🎯',
    decision: '◇',
    task: '☐',
    gateway: '◇',
    event: '○',
    table: '📊',
    start: '▶️',
    end: '⏹️',
    process: '☐',
    root: '🎯',
    branch: '🌿',
    person: '👤',
    team: '👥',
  }
  return iconMap[nodeType] || '○'
}
